/* eslint-disable no-restricted-globals */
/**
 * Editor related commands
 */
import {MAX_GETTY_IMAGES_PER_EMAIL, MAX_GETTY_VIDEOS_PER_EMAIL, MULTI_MEDIA_SOURCE_GETTY, MultiMediaUploadSource} from '@Libraries/user-media-library';
import {getUserId, isUserLoggedIn} from '@Libraries/user.library';
import {disableVersionHistoryCta, enableVersionHistoryCta, isVersionHistoryAccessible} from '../version-history';
import {openMessageModal} from '@Components/modals/message-modal';
import type {CanMoveResult, Component, ComponentDefinition, Editor, StyleProps} from 'grapesjs';
import juice from 'juice';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';
import type {EmbeddedEditorAction, EmbeddedEditorOpenDesignConfig} from '@Libraries/embedded-editor';
import {closeEmbeddedEditor, closeEmbeddedEditorWithoutCallback, EmbeddedEditorElementsToHide, EmbeddedEditorMessageType} from '@Libraries/embedded-editor';
import type {ElementData, ImageData, StickerData, VideoData} from '@Libraries/add-media-library';
import {openStockMediaModal} from '@Modals/stock-modal';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import {removeImageBackground} from '@Utils/image.util';
import {getRemovedImageBackgroundScreenURL, UserImageSource} from '@Libraries/user-image-library';
import {openErrorModal} from '@Modals/error-modal';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {openMediaTypeModal} from '@Modals/media-type-modal/media-type-modal';
import {MediaType} from '@Panels/media-type-panel/media-type-panel';
import type {ErrorType, SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiTextErrorTypes, PromptBoxConfigOptionType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';
import {initMergeTagsDropdown, TOOLBAR_MERGE_TAGS_LIST_ID} from '@Global/email-maker';
import {capitalizeFirstLetter, getEnumMember, getUniqueId, lowerCaseFirstLetter} from '@Utils/string.util';
import {unSelectedItemsForMultipleLists} from '@Components/list/list-reducer';
import {addTagToMap, populateTagsForComponent, removeAllTagsForComponent} from '@Components/merge-tags/merge-tags-reducer';
import {updateSocialLinkSuggestions} from '@Components/email-maker-social-link-suggestions-dropdown/email-maker-social-link-suggestions-dropdown-reducer';
import type {
  AsyncGetPreviewSuccessResponse,
  AutoUpdateEmailContentParams,
  BasicComponentCommandProps,
  CanUseBypassedEmailResponse,
  ComponentloadingOverlayStyle,
  EmailMakerPuppeteerConfig,
  EmailPreviewResponse,
  UserSignupFormLinkData,
} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {
  AsyncGetPreviewErrorCodes,
  ComponentloadingOverlayStylesTarget,
  EMAIL_MAKER_AI_REDESIGN_LOADING_TOAST,
  EmailMakerPuppeteerFlow,
  EmailOverlayStyle,
  OnLoadUrlParamsConfig,
  TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR,
  UpsertComponentMethod,
} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {PMWStockImageSource} from '@Libraries/pmw-stock-media-library.types';
import {getPMWMergeTagRegex} from '@Libraries/merge-tags-library';
import type {SocialLinkSuggestions} from '@Components/email-maker-social-link-suggestions-dropdown/email-maker-social-link-suggestions-dropdown.types';
import {colorToHex} from '@Utils/color.util';
import {storeAppliedPaletteInHtml} from '@Libraries/auto-branding-email-maker';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import {GA4EventName} from '@Libraries/ga-events';
import {StockSources} from '@Libraries/pmw-stock-media-library';
import {UserVideoSource} from '@Libraries/user-video-library';
import type {EmbedVideosEmailmakerInputModalProps} from '@Modals/embed-videos-emailmaker-input-modal';
import {openEmbedVideosEmailmakerModal} from '@Modals/embed-videos-emailmaker-input-modal';
import {getAssetUrl} from '@Utils/s3.util';
import {noop} from '@Utils/general.util';
import type {EmailCampaignAiRedesignSource, EmailCampaignAiRegenerateImageSource} from '@/libraries/ai-email-campaign-content-library';
import {EmailCampaignAiRewriteSource} from '@/libraries/ai-email-campaign-content-library';
import {notifyPuppeteer} from '@Libraries/puppeeter.library';
import {PALETTE_APPLIED_EVENT} from '@/email-maker/grapesjs-email-editor-plugin/src/custom-style-types/auto-branding';
import {initiatePosterMorphLoading, terminatePosterMorphLoading} from '@PosterWhiteboard/poster/poster-init';
import type {EditableComponentsMap, SelectedPromptOptionValues} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import {AiModel, EmojiUsage, PromptOption, RedesignMood} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import type {ColorPaletteInfo, ColorPalettePill} from '@Libraries/color-palettes.library';
import {ColorPaletteType} from '@Libraries/color-palettes.library';
import type {ApplyColorPaletteToEmailServerResponse} from '@Components/auto-branding/auto-branding.types';
import he from 'he';
import type {TextComponentData} from '@Libraries/email-campaigns-library';
import {convertComponentIfNeeded, getEmailTextWithCcid, isMobileWidth} from '@Libraries/email-campaigns-library';
import {
  gatherImagesData,
  getEmailContextForImageGeneration,
  initiateAiRedesignImageRegeneration,
  pollAndReplaceGeneratedImages,
  settleEmailCampaignAiRedesignImageRegeneration,
} from '@Libraries/email-campaign-ai-image-regeneration-library';
import {getTeamCredits, refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {AiTextPromptBoxError} from '@Libraries/ai-text-prompt-box-library';
import {setIsLoading, setLastRunCreditsCharged} from '@Components/email-maker-ai-redesign-prompt-box/email-maker-ai-redesign-prompt-box-reducer';
import {applyAiRedesignChanges, handleAiRedesignError, handleAiRedesignFeedback, saveAiRedesignGenerationResults} from '@Libraries/email-campaign-ai-redesign-library';
import {fetchAiRewriteRecentPrompt} from '@Libraries/email-campaign-ai-rewrite-library';
import type {PosterPageData, PosterPageHashedIdInfo} from '@Libraries/poster-library';
import {getPostersPageIds} from '@Libraries/poster-library';
import {openDesignSelector, openDesignSelectorLegacy} from '@/email-maker/grapesjs-email-editor-plugin/src/helpers/design-selector-helper';
import {finishLoading} from '@/email-maker/grapesjs-email-editor-plugin/src/helpers/editor-helper';

interface CustomCodeViewer {
  id: string;
  view: object;
  init: (config: object) => void;
  setContent: (config: any) => void;
  set: (config: object) => void;
  editor: CodeViewerEditor;
}

interface CodeViewerEditor {
  setOption: (opt: string, value: number) => void;
  refresh: VoidFunction;
  getValue: (opt?: string) => string;
}

interface BannerOptions {
  message: string;
  style: BannerStyle;
  icon?: string;
  cta?: VoidFunction;
  ctaText?: string;
}

export enum BannerStyle {
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  SUCCESS = 'success',
  AI = 'ai',
}

export default (opt: PrimaryConfigOptions): void => {
  const STYLE_MANAGER_CONTAINER = $('.js-style-manager-container');
  const IDEMAIL_PREVIEW_DIALOG = 'pmw-email-preview-dialog';
  const PUPPETEER_NOTIFIER_ID = 'editor-execution-complete';

  const MAILTO_HREF = 'mailto:';
  const TELEPHONE_HREF = 'tel:';
  const HTTP_PROTOCOL = 'http://';
  const CMDS = window.PMW.emails.commands;

  const {editor} = opt;
  const cmds = editor.Commands;

  initPredefinedEditorCommands(opt);
  initPredefinedStorageCommands(opt);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  cmds.add(CMDS.INIT_COLLAPSED_STYLES, (): void => {});

  cmds.add(CMDS.INIT_STYLE_FOR_LINK_COLOR, (editorInstance: Editor, _sender: Component, opts: BasicComponentCommandProps): void => {
    if (opts.component?.get('type') !== window.PMW.emails.components.text.PMW_TEXT) {
      return;
    }

    const isLinkColorApplied = editorInstance.getSelected()?.getEl()?.hasAttribute(window.PMW.emails.styles.LINK_COLOR);

    if (isLinkColorApplied) {
      $(`.gjs-sm-property__${window.PMW.emails.styles.LINK_COLOR}`)?.removeClass('-collapsed');
    } else {
      $(`.gjs-sm-property__${window.PMW.emails.styles.LINK_COLOR}`)?.addClass('-collapsed');
    }
  });

  cmds.add(CMDS.GET_EMAIL_HTML_PROMISE, async (): Promise<EmailPreviewResponse> => {
    const internalHTML = editor.runCommand(CMDS.GET_INLINED_HTML, {}) as string;
    const rulesString = editor.runCommand(CMDS.GET_MEDIA_QUERIES, {}) as string;

    return window.PMW.writeLocal('emailmaker/getSanitizedEmailTemplateHTML', {
      htmlContent: internalHTML,
      mediaQueries: rulesString,
    });
  });

  cmds.add(CMDS.GET_INLINED_HTML_FOR_MARKETING, (): Promise<EmailPreviewResponse> => {
    const internalHTML = editor.runCommand(CMDS.GET_INLINED_HTML, {}) as string;
    const rulesString = editor.runCommand(CMDS.GET_MEDIA_QUERIES, {}) as string;

    return window.PMW.writeLocal('emailmarketing/getBaseMarketingEmailTemplateHTML', {
      htmlContent: internalHTML,
      mediaQueries: rulesString,
    });
  });

  cmds.add(
    CMDS.IS_VALID_FOR_LINK,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        url: string;
      }
    ) => {
      const value = opts.url;
      const possibleEmailValue = value.toLowerCase().replace(MAILTO_HREF, '');
      const isValidURL = isURL(value);
      const isValidNum = isPhoneNumber(value) && !isValidURL;
      const isValidEmail = window.PMW.util.isValidEmail(possibleEmailValue);

      return isValidEmail || isValidNum || isValidURL;
    }
  );

  cmds.add(CMDS.IS_VIDEO_LINK_WRAPPER, (_editorInstance: Editor, _sender: Component, opts: BasicComponentCommandProps) => {
    if (!opts.component) {
      return false;
    }

    const attributes = opts.component.getAttributes();
    return attributes['data-is-pmw-embedded-video'] === 1;
  });

  cmds.add(
    CMDS.IS_VALID_URL,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        url: string;
      }
    ) => {
      const value = opts.url;

      return isURL(value);
    }
  );

  cmds.add(
    CMDS.GET_VALIDATED_URL,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        url: string;
      }
    ) => {
      let value = opts.url;
      const possibleEmailValue = value.toLowerCase().replace(MAILTO_HREF, '');
      const isValidURL = isURL(value);
      const isValidNum = isPhoneNumber(value) && !isValidURL;
      const isValidEmail = window.PMW.util.isValidEmail(possibleEmailValue);

      if (isValidEmail || doesInputStartWithMailto(value)) {
        value = formatEmail(value);
      } else if (isValidNum) {
        value = formatTelephoneNum(value);
      } else if (isValidURL) {
        value = formatURL(value);
      }

      return value.trim();
    }
  );

  cmds.add(CMDS.SHOW_BANNER, (_editorInstance: Editor, _sender: object, opts: BannerOptions): void => {
    const prepEditorForBannerStripAddition = (): void => {
      $('.html-editor-canvas-container').addClass('message-banner');
      $('.html-editor-canvas-box').addClass('message-banner');
      $('.preview-option-actions').addClass('hidden-tablet hidden-phone');
      $('.email-editor-responsive-toolbar').addClass('hidden-tablet hidden-phone');
      $('.mobile-editor-options').addClass('hidden-tablet hidden-phone');
    };

    const getStyleClass = (style: BannerStyle): string => {
      switch (style) {
        case BannerStyle.ERROR:
          return '-error';
        case BannerStyle.WARNING:
          return '-warning';
        case BannerStyle.INFO:
          return '-neutral';
        case BannerStyle.SUCCESS:
          return '-success';
        case BannerStyle.AI:
          return 'bg-ai';
        default:
          return '-neutral';
      }
    };

    if ($('.message-notification').length > 0) {
      return;
    }

    const bannerStripContainer = $('.js-message-strip');
    bannerStripContainer.removeClass('hidden');
    bannerStripContainer.addClass(getStyleClass(opts.style));

    const messageElement = document.createElement('p');
    messageElement.className = `message-notification ${opts.style === BannerStyle.AI ? 'content-ai' : ''}`;
    messageElement.innerHTML = opts.message;

    const iconElement = document.createElement('i');
    iconElement.className = `${opts.icon} size-icon-16 banner-icon`;

    messageElement.prepend(iconElement);
    bannerStripContainer.html(messageElement);

    if (opts.cta && opts.ctaText) {
      const ctaElement = document.createElement('button');
      ctaElement.textContent = opts.ctaText;
      ctaElement.className = `banner-cta ${opts.style === BannerStyle.AI ? 'content-ai' : 'content-link'}`;
      ctaElement.onclick = (): void => {
        opts.cta?.();
      };
      bannerStripContainer.append($(ctaElement));
    }

    prepEditorForBannerStripAddition();
  });

  cmds.add(CMDS.HIDE_BANNER, () => {
    const prepEditorForErrorStripRemoval = (): void => {
      $('.html-editor-canvas-container').removeClass('message-banner');
      $('.html-editor-canvas-box').removeClass('message-banner');
      $('.preview-option-actions').removeClass('hidden-tablet hidden-phone');
      $('.email-editor-responsive-toolbar').removeClass('hidden-tablet hidden-phone');
      $('.mobile-editor-options').removeClass('hidden-tablet hidden-phone');
    };

    const messageBanner = $('.message-notification');

    if (messageBanner.length > 0) {
      messageBanner.remove();
      $('.js-message-strip').addClass('hidden');
    }

    prepEditorForErrorStripRemoval();
  });

  cmds.add(CMDS.FIX_UPPER_CASE_LIMIT, (editorInstance: Editor) => {
    function toSentenceCase(str: string): string {
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    const mainWrapper = editorInstance.runCommand(CMDS.GET_MAIN_WRAPPER) as Component;
    const textComponents = mainWrapper?.findType(window.PMW.emails.components.text.PMW_TEXT);

    if (!textComponents) {
      return;
    }

    textComponents.forEach((component) => {
      component.components(toSentenceCase(component.getEl()?.innerHTML ?? ''));
    });

    void editorInstance.store();
  });

  cmds.add(CMDS.OPEN_CUSTOM_HTML_DIALOG, () => {
    const IDEMAIL_IMPORT_DIALOG = 'pmw-email-import-dialog';
    const buttons: Record<string, VoidFunction> = {};
    const htmlContent = '<textarea class="js-imported-email-content imported-email-content"></textarea>';

    buttons.Upload = (): void => {
      const html = $(`#${IDEMAIL_IMPORT_DIALOG}`).find('.js-imported-email-content').val() as string;
      editor.setComponents(html);
      window.PMW.closeModalDialog(IDEMAIL_IMPORT_DIALOG);
    };

    const modal = window.PMW.openModalDialog(IDEMAIL_IMPORT_DIALOG, 'Import Email HTML', buttons, true, 'full-screen');
    modal.find('.content').html(htmlContent);
  });

  cmds.add(CMDS.FIX_WRAPPER_WIDTH, (editorInstance: Editor) => {
    const wrapper = editorInstance.getWrapper();

    if (!wrapper) {
      return;
    }

    const pmwWrapper = wrapper.find(`[data-gjs-type="${window.PMW.emails.components.container.PMW_WRAPPER}"]`)[0];

    if (!pmwWrapper) {
      return;
    }
    const wrapperStyles = pmwWrapper.getStyle();
    wrapperStyles.width = '100%';
    pmwWrapper.setStyle(wrapperStyles);
  });

  cmds.add(CMDS.GET_IMAGE_ASSET_HASHED_NAMES, () => {
    return getHashedFileNamesFromType(editor, window.PMW.emails.components.graphic.PMW_IMAGE);
  });

  cmds.add(CMDS.GET_VIDEO_ASSET_HASHED_NAMES, () => {
    return getHashedFileNamesFromType(editor, window.PMW.emails.components.graphic.PMW_VIDEO);
  });

  cmds.add(CMDS.DISABLE_BLOCKS, () => {
    $('.html-editor-blocks-manager').addClass('disabled');
  });

  cmds.add(CMDS.ENABLE_BLOCKS, () => {
    $('.html-editor-blocks-manager').removeClass('disabled');
  });

  cmds.add(CMDS.TOTAL_COMPONENT_COUNT, () => {
    let count = 0;
    editor.getWrapper()?.onAll(() => {
      count += 1;
    });
    return count;
  });

  cmds.add(CMDS.GET_MEDIA_QUERIES, () => {
    const rules = editor.Css.getRules();
    const rulesArray = rules.map((rule) => {
      return rule.toCSS();
    });
    return rulesArray.join('\r\n');
  });

  cmds.add(CMDS.GET_DESIGN_IDS, () => {
    const ids = new Set();
    editor.getWrapper()?.onAll((component) => {
      if (component.is(window.PMW.emails.components.graphic.PMW_DESIGN)) {
        const id = component.get(window.PMW.emails.props.DESIGN_ID) as string | undefined;
        if (id) {
          ids.add(id);
        }
      }
    });

    return Array.from(ids).join(',') || '';
  });

  function doesComponentTypeHaveGif(componentName: string): boolean {
    const allComps = editor.getWrapper()?.findType(componentName);
    let hasGif = false;

    if (!allComps) {
      return false;
    }

    allComps.forEach((component) => {
      if (hasGif) {
        return;
      }

      const src: string = (component.get('src') || component.getAttributes().src || '') as string;
      const srcWithoutArgs: string = src.split('?')[0];
      if (/\.gif$/gi.test(srcWithoutArgs)) {
        hasGif = true;
      }
    });

    return hasGif;
  }

  cmds.add(CMDS.DOES_EMAIL_CONTAIN_GIF, () => {
    return (
      doesComponentTypeHaveGif(window.PMW.emails.components.graphic.PMW_VIDEO) ||
      doesComponentTypeHaveGif(window.PMW.emails.components.graphic.PMW_IMAGE) ||
      doesComponentTypeHaveGif(window.PMW.emails.components.graphic.PMW_STICKER) ||
      doesComponentTypeHaveGif(window.PMW.emails.components.graphic.PMW_DESIGN)
    );
  });

  cmds.add(CMDS.GET_TEMPLATE_NAME, (): string => {
    const wrapperComp = editor.getWrapper()?.findType(window.PMW.emails.components.container.PMW_WRAPPER)[0];
    return (wrapperComp?.get(window.PMW.emails.props.TEMPLATE_NAME) as string) || window.PMW.emails.NEW_TEMPLATE_NAME;
  });

  cmds.add(CMDS.GET_PREMIUM_DESIGN_IDS, (): string[] => {
    const myDesigns = editor.getWrapper()?.findType(window.PMW.emails.components.graphic.PMW_DESIGN);
    if (!myDesigns) {
      return [];
    }

    const premiumDesigns = myDesigns.filter((design) => {
      return design.get(window.PMW.emails.props.IS_PREMIUM_DESIGN) && editor.runCommand(CMDS.DOES_PMW_DESIGN_BELONG_TO_USER, {component: design});
    });

    const premiumDesignIDs = premiumDesigns.map((design) => {
      return design.get(window.PMW.emails.props.DESIGN_ID) as string;
    });

    return [...new Set(premiumDesignIDs)];
  });

  cmds.add(CMDS.TOGGLE_BUY_GETTY_DESIGN_MESSAGE, () => {
    const premiumDesignIds = editor.runCommand(window.PMW.emails.commands.GET_PREMIUM_DESIGN_IDS) as string[];
    const gettyLabelEl = $('.js-getty-label');
    const gettyDesignLinkEl = $('.js-getty-design-link');

    if (premiumDesignIds.length > 0) {
      gettyLabelEl.removeClass('_hidden');
      gettyDesignLinkEl.attr('href', window.PMW.util.site_url(`posterbuilder/load/${premiumDesignIds[0]}?downloadDlg=1&hp=1`));
    } else {
      gettyLabelEl.addClass('_hidden');
    }
  });

  cmds.add(CMDS.GET_VIDEO_COUNT, (): number => {
    const wrapper = editor.getWrapper();
    if (!wrapper) {
      return 0;
    }

    const videos = wrapper.findType(window.PMW.emails.components.graphic.PMW_VIDEO);
    const tables = wrapper.findType(window.PMW.emails.components.table.PMW_TABLE);
    const sections = wrapper.findType(window.PMW.emails.components.table.PMW_SECTION);
    const cells = wrapper.findType(window.PMW.emails.components.table.PMW_CELL);
    const compsWithBackgrounds = [...tables, ...sections, ...cells];
    const compsWithVideoBackgrounds = compsWithBackgrounds.filter((comp) => {
      const bg = comp.getStyle()['background-image'] as string;
      return bg && /\.gif/gi.test(bg);
    });

    return videos.length + compsWithVideoBackgrounds.length;
  });

  cmds.add(CMDS.ATTACH_WINDOW_RESIZE_LISTENER, {
    run() {
      window.addEventListener('resize', window.PMW.debounce(resizeEditorBlocksManager, 10), {passive: true});
      resizeEditorBlocksManager();

      function resizeEditorBlocksManager(): void {
        const NAV_HEIGHT_PX = 60;
        const STYLE_MANAGER_PADDING = 16;
        const STYLE_MANAGER_BORDER = 1;

        const wh = $(window).height();
        // eslint-disable-next-line
        // @ts-ignore
        const bodyHeight = wh - NAV_HEIGHT_PX;
        const styleManagerHeight = bodyHeight - 2 * (STYLE_MANAGER_PADDING + STYLE_MANAGER_BORDER);

        const bodyHeightVal = `${bodyHeight}px`;
        const styleManagerHeightVal = `${styleManagerHeight}px`;

        $('#bd').css('height', bodyHeightVal);
        STYLE_MANAGER_CONTAINER.css('height', styleManagerHeightVal);
      }
    },
  });

  cmds.add(CMDS.FINISH_LOADING_EDITOR, {
    run(grapesjsEditor) {
      $('.email-preloader').fadeOut(800);

      setDeviceOnLoad(grapesjsEditor);
      enableComponentOutlines(grapesjsEditor);
      updateShareBtnIcons(grapesjsEditor);
      grapesjsEditor.runCommand(CMDS.ATTACH_WINDOW_RESIZE_LISTENER);
    },
  });

  cmds.add(CMDS.UPDATE_SHARE_BUTTON_ICON, (_editorInstance: Editor, _sender: Component, opts: {isSharedInTeam: boolean}) => {
    const shareBtns = document.querySelectorAll('.js-share-in-team-icon');

    shareBtns.forEach((shareBtn) => {
      if (opts.isSharedInTeam) {
        shareBtn.classList.remove('icon-lock');
        shareBtn.classList.add('icon-group-add');
      } else {
        shareBtn.classList.add('icon-lock');
        shareBtn.classList.remove('icon-group-add');
      }
    });
  });

  cmds.add(CMDS.INITIATE_BLOCK_MANAGER, () => {
    const container = $('.js-blocks-manager');
    const blocksManagerContainerId = window.PMW.emails.blocks.ID_BLOCKS_MANAGER_CONTAINER;
    let blocksManagerContainer = document.getElementById(blocksManagerContainerId);

    blocksManagerContainer = document.createElement('div');
    blocksManagerContainer.id = blocksManagerContainerId;

    window.PMW.renderEmailMakerBlockManager(blocksManagerContainer, {gjsEditor: editor});

    container.append(blocksManagerContainer);
  });

  cmds.add(CMDS.DOES_PMW_DESIGN_BELONG_TO_USER, (_editorInstance: Editor, _sender: Component, opts: BasicComponentCommandProps): boolean => {
    const currentUserId = getUserId();

    if (!currentUserId) {
      return false;
    }

    if (!opts.component) {
      return false;
    }

    const designCreatorId = opts.component.get(window.PMW.emails.props.DESIGN_CREATOR_ID) as number;

    if (!designCreatorId) {
      return false;
    }

    return designCreatorId === currentUserId;
  });

  cmds.add(CMDS.INIT_LOADING_TIPS, {
    run() {
      const LOADING_TIP_CYCLE = 5000;

      const intervalId = setInterval(() => {
        const displayedLoadingTip = $('.js-loading-tip.-visible');
        const nextLoadingTip = displayedLoadingTip.next('.js-loading-tip');

        if (nextLoadingTip.length > 0) {
          displayedLoadingTip.removeClass('-visible');
          nextLoadingTip.addClass('-visible');
        } else {
          clearInterval(intervalId);
        }
      }, LOADING_TIP_CYCLE);
    },
  });

  /**
   * update any template properties that might have changed in the DB but not in loaded JSON
   */
  cmds.add(CMDS.SYNC_UPDATED_DB_CONTENT, (editorInstance: Editor): void => {
    const selected = editorInstance.getSelected();

    syncTemplateName(editorInstance);

    if (selected) {
      editorInstance.selectToggle(selected);
      editorInstance.selectToggle(selected);
    }
  });

  cmds.add(CMDS.ZOOM_OUT, (editorInstance: Editor) => {
    const editorBody = editorInstance.Canvas.getBody();
    if (!editorBody) {
      return;
    }
    window.PMW.emails.ZOOM_VALUE -= window.PMW.emails.ZOOM_STEP;
    editorBody.style.transform = `scale(${window.PMW.emails.ZOOM_VALUE})`;

    if (window.PMW.emails.ZOOM_VALUE <= window.PMW.emails.DEFAULT_ZOOM) {
      editorBody.style.height = `${window.PMW.emails.ZOOM_VALUE * 100}%`;
    }

    editorInstance.runCommand(CMDS.FIX_LOADING_OVERLAY_FOR_EMAIL_SIZE);
  });

  cmds.add(CMDS.ZOOM_IN, (editorInstance: Editor) => {
    const editorBody = editorInstance.Canvas.getBody();
    if (!editorBody) {
      return;
    }
    window.PMW.emails.ZOOM_VALUE += window.PMW.emails.ZOOM_STEP;
    editorBody.style.transform = `scale(${window.PMW.emails.ZOOM_VALUE})`;

    if (window.PMW.emails.ZOOM_VALUE <= window.PMW.emails.DEFAULT_ZOOM) {
      editorBody.style.height = `${window.PMW.emails.ZOOM_VALUE * 100}%`;
    }

    editorInstance.runCommand(CMDS.FIX_LOADING_OVERLAY_FOR_EMAIL_SIZE);
  });

  cmds.add(CMDS.FIT_TO_SCREEN, (editorInstance: Editor) => {
    const editorBody = editorInstance.Canvas.getBody();
    if (!editorBody) {
      return;
    }
    editorBody.style.transform = `scale(${window.PMW.emails.DEFAULT_ZOOM})`;

    window.PMW.emails.ZOOM_VALUE = window.PMW.emails.DEFAULT_ZOOM;
    editorBody.style.height = '100%';

    editorInstance.runCommand(CMDS.FIX_LOADING_OVERLAY_FOR_EMAIL_SIZE);
  });

  cmds.add(CMDS.FIX_LOADING_OVERLAY_FOR_EMAIL_SIZE, (editorInstance: Editor) => {
    const overlay = editorInstance.runCommand(CMDS.GET_LOADING_OVERLAY_FOR_EMAIL) as Component;
    const wrapper = editorInstance.runCommand(CMDS.GET_MAIN_WRAPPER) as Component;
    const footer = editorInstance.runCommand(CMDS.GET_READ_ONLY_FOOTER) as Component;

    if (!overlay || !wrapper || !footer) {
      return;
    }

    const wrapperHeight = wrapper.getEl()?.offsetHeight;
    const footerHeight = footer.getEl()?.offsetHeight;

    const height = wrapperHeight && footerHeight ? `${(wrapperHeight + footerHeight).toString()}px` : '100%';

    overlay.setStyle({height});
  });

  cmds.add(CMDS.UPDATE_UNDO_REDO_STATES, {
    run(editorInstance) {
      const um = editorInstance.UndoManager;
      const undoBtnSelector = $('.js-nav-email-editor-undo');
      const redoBtnSelector = $('.js-nav-email-editor-redo');

      if (um.hasUndo()) {
        undoBtnSelector.removeClass('-disabled');
      } else {
        undoBtnSelector.addClass('-disabled');
      }

      if (um.hasRedo()) {
        redoBtnSelector.removeClass('-disabled');
      } else {
        redoBtnSelector.addClass('-disabled');
      }
    },
  });

  cmds.add(CMDS.ON_ALL_COMPONENTS_LOADED, {
    run(editorInstance) {
      if (window.PMW.emails.IS_IN_VERSION_HISTORY_MODE) {
        return;
      }
      if (isUndoRedoInProgress(editorInstance)) {
        return;
      }
      editorInstance.getModel().skip(() => {
        editorInstance.runCommand(CMDS.RELOAD_PREMIUM_BACKGROUNDS);
        editorInstance.runCommand(CMDS.RELOAD_ALL_DESIGNS);
      });
    },
  });

  /**
   * GrapesJS exposes no public "is undo/redo in progress" getter and its typings don't surface the flag, so we read backbone-undo's internal
   * `isCurrentlyUndoRedoing`. That flag lives on the UndoStack (the collection `getStack()` returns),
   * not on the UndoManager — hence reading it off `getStack()`, via as-unknown-as to reach the untyped internal.
   */
  function isUndoRedoInProgress(editorInstance: Editor): boolean {
    const undoStack = editorInstance.UndoManager.getStack() as unknown as {isCurrentlyUndoRedoing?: boolean};
    return undoStack.isCurrentlyUndoRedoing === true;
  }

  cmds.add(CMDS.SET_DEVICE_DESKTOP, {
    run() {
      // window.PMW.gtm.trackGA4CustomEvent('EmailMaker_DesktopPreviewOn');
      setActiveDevice(window.PMW.emails.DEVICE_DESKTOP);
      setContainerClassForDevice(window.PMW.emails.DEVICE_DESKTOP);
    },
  });

  cmds.add(CMDS.SET_DEVICE_TABLET, {
    run() {
      // Tracking temporarily disabled on 8th Nov 2023
      // window.PMW.gtm.trackGA4CustomEvent('EmailMaker_TabletPreviewOn');
      setActiveDevice(window.PMW.emails.DEVICE_TABLET);
      setContainerClassForDevice(window.PMW.emails.DEVICE_TABLET);
    },
  });

  cmds.add(CMDS.SET_DEVICE_MOBILE, {
    run() {
      // Tracking temporarily disabled on 8th Nov 2023
      // window.PMW.gtm.trackGA4CustomEvent('EmailMaker_MobilePreviewOn');
      setActiveDevice(window.PMW.emails.DEVICE_MOBILE);
      setContainerClassForDevice(window.PMW.emails.DEVICE_MOBILE);
    },
  });

  cmds.add(CMDS.CLOSE_EMBEDDED_EDITOR, {
    run(_editorInstance: Editor, _sender, opts: {isSilentClose: boolean}) {
      if (opts.isSilentClose) {
        closeEmbeddedEditorWithoutCallback();
      } else {
        closeEmbeddedEditor();
      }
    },
  });

  cmds.add(CMDS.SAVE_AND_CLOSE_EMBEDDED_EDITOR, {
    async run(editorInstance: Editor) {
      if (!isUserLoggedIn()) {
        editorInstance.runCommand(CMDS.AUTHENTICATE_USER, {
          cb: (): void => {
            editorInstance.runCommand(CMDS.SAVE_AND_CLOSE_EMBEDDED_EDITOR, {});
          },
        });
        return;
      }

      await editorInstance.runCommand(CMDS.SAVE_TO_CACHE, {});

      window.parent.postMessage(`${EmbeddedEditorMessageType.SAVE}~${window.PMW.idEmailTemplate}`, '*');
      editorInstance.runCommand(CMDS.CLOSE_EMBEDDED_EDITOR, {isSilentClose: true});
    },
  });

  cmds.add(CMDS.CLOSE_PREVIEW_DROPDOWN, {
    run() {
      const checkbox = document.getElementById('nav-hamburger-menu') as HTMLInputElement;
      if (checkbox) {
        checkbox.checked = false;
      }
    },
  });

  cmds.add(CMDS.OPEN_CALENDAR_POPUP, {
    run() {
      openCalendarPopup();
    },
  });

  cmds.add(CMDS.OPEN_PREVIEW_DIALOG, {
    run() {
      void openPreviewDialog();
    },
  });

  cmds.add(CMDS.UPDATE_PUBLISH_CTA_PLACEMENT, {
    run(editorInstance) {
      if (editorInstance.runCommand(CMDS.GET_PREFILLED_SCHEDULE_DATE)) {
        const scheduleBtn = $('.js-schedule-email-campaign');
        const publishBtn = $('.js-publish-email-btn');

        scheduleBtn.toggleClass('-transparent -checkout');
        publishBtn.toggleClass('-transparent -checkout');

        $('.schedule-btn-holder').css('order', 1);
        publishBtn.css('order', 0);
      }
    },
  });

  cmds.add(CMDS.GET_PREFILLED_SCHEDULE_DATE, {
    run() {
      const prefilledDateTimestamp = window.PMW.getCacheItem(getCacheKeyForScheduling()) as string;

      if (prefilledDateTimestamp) {
        const schedulingDate = Date.parse(prefilledDateTimestamp);

        if (schedulingDate) {
          return new Date(schedulingDate);
        }
      }

      return null;
    },
  });

  cmds.add(CMDS.DELETE_PREFILLED_SCHEDULE_DATE, {
    run() {
      window.PMW.deleteCacheItem(getCacheKeyForScheduling());
    },
  });

  cmds.add(CMDS.INIT_CALENDAR_DIALOG, {
    run() {
      window.addEventListener('mouseup', (e) => {
        const calendarContainer = $(`.js-calendar-popup`);
        const target = e.target as Node;

        if (calendarContainer.length > 0 && !calendarContainer[0].contains(target)) {
          hideCalendarContainerPopup(calendarContainer);
        }
      });
    },
  });

  /**
   *
   * @param calendarContainer
   */
  function hideCalendarContainerPopup(calendarContainer: JQuery): void {
    calendarContainer.addClass('_hidden');
  }

  function openCalendarPopup(): void {
    const calendarContainer = $(`.js-calendar-popup`);
    if (calendarContainer[0].classList.contains('_hidden')) {
      calendarContainer.removeClass('_hidden');
      const minDate = new Date();
      minDate.setMinutes(minDate.getMinutes() + window.PMW.emails.MINUTES_STEP);
      const opts: {
        prefilledDate?: Date;
        skipDateSelector?: boolean;
        onSubmit?: (dateTimeValue: Date) => void;
        minDate: Date;
      } = {
        onSubmit: onSubmitFromCalendar,
        minDate,
      };
      const prefilledDate = editor.runCommand(CMDS.GET_PREFILLED_SCHEDULE_DATE) as Date;

      if (prefilledDate) {
        opts.prefilledDate = prefilledDate;
        opts.skipDateSelector = true;
      }

      window.PMW.openCalendarPopup(calendarContainer[0], opts);
    } else {
      calendarContainer.addClass('_hidden');
    }
  }

  function getCacheKeyForScheduling(): string {
    return 'emc_schedule';
  }

  /**
   *
   * @param {Date} dateTimeValue
   */
  function onSubmitFromCalendar(dateTimeValue: Date): void {
    $(`.js-calendar-popup`).addClass('_hidden');
    window.PMW.emails.IS_SCHEDULED = true;
    window.PMW.emails.SCHEDULED_TIME = dateTimeValue.toISOString();
    editor.runCommand(window.PMW.emails.commands.SAVE_AND_PUBLISH);
  }

  cmds.add(CMDS.OPEN_MORE_OPTIONS, {
    run(editorInstance) {
      const dlg = window.PMW.openModalDialog(
        window.PMW.emails.MORE_OPTIONS_DIALOG,
        window.i18next.t('pmwjs_more_options'),
        {},
        true,
        '-modal-500 -centered-header -radius-16',
        false
      );
      const content = dlg.find('.content');

      content.addClass('spacing-p-t-3');

      // eslint-disable-next-line
      // @ts-ignore
      void $.get(
        window.PMW.util.site_url('emailmaker/getMoreOptionsDialogHTML'),
        {
          idEmailMarketingCampaign: window.PMW.idEmailCampaign,
        },
        (r: AjaxSuccessResponse) => {
          if (r.status === 'success') {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-argument
            content.html(r.data.html);
            if (isVersionHistoryAccessible()) {
              enableVersionHistoryCta();
            }
            editorInstance.runCommand(CMDS.TOGGLE_BUY_GETTY_DESIGN_MESSAGE);
          }
        }
      );
    },
  });

  cmds.add(CMDS.CLOSE_MORE_OPTIONS, {
    run() {
      window.PMW.closeModalDialog(window.PMW.emails.MORE_OPTIONS_DIALOG);
    },
  });

  cmds.add(CMDS.OPEN_EXPORT_OPTIONS, {
    run() {
      const dlg = window.PMW.openModalDialog(
        window.PMW.emails.EXPORT_OPTIONS_DIALOG_MOBILE,
        window.i18next.t('pmwjs_export'),
        {},
        true,
        '-modal-500 -centered-header -radius-16',
        false
      );
      const content = dlg.find('.content');

      content.addClass('spacing-p-t-3');

      void window.PMW.readLocal('emailmaker/getExportOptionsDialogHTML', {isMobile: true}).then((response: {html: string}) => {
        if (response.html) {
          content.html(response.html);
        }
      });
    },
  });

  async function openPreviewDialog(): Promise<void> {
    const btn: Record<string, () => Promise<void>> = {};
    btn[window.i18next.t('pmwjs_send')] = async (): Promise<void> => {
      const btn = $(`#${IDEMAIL_PREVIEW_DIALOG}`).find('.publishCta');
      const initialText = btn.html();
      btn.text(window.i18next.t('pmwjs_loading'));
      await editor.runCommand(window.PMW.emails.commands.SAVE_AND_PUBLISH);
      btn.html(initialText);
    };

    const modal = window.PMW.openModalDialog(
      IDEMAIL_PREVIEW_DIALOG,
      window.i18next.t('pmwjs_preview'),
      btn,
      true,
      'email-editor-preview-dialog -mobile-fullwidth-footer -radius-16'
    );

    const r = (await editor.runCommand(CMDS.GET_EMAIL_HTML_PROMISE)) as EmailPreviewResponse;
    const {html} = r;

    const publishBtn = modal.find('button').first();
    const publishBtnIcon = '<i class="icon-publish-arrow pmw-icon size-icon-16 spacing-m-l-2"></i>';
    publishBtn.append(publishBtnIcon);
    publishBtn.addClass('publishCta');
    publishBtn.parent().addClass('spacing-m-0');

    // eslint-disable-next-line
    // @ts-ignore
    void $.get(window.PMW.util.site_url(`emailmaker/getPreviewDialogHTML/`), null, (res: AjaxSuccessResponse) => {
      if (res.status === 'success') {
        window.PMW.gtm.trackGA4CustomEvent('EmailMaker_EmailPreviewed');

        const iframeParent = document.createElement('iframe');
        iframeParent.id = 'email-template-preview-iframe';
        iframeParent.height = '100%';
        iframeParent.width = '100%';
        iframeParent.sandbox.add('allow-same-origin', 'allow-popups', 'allow-popups-to-escape-sandbox');

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        modal.find('.content').html(res.data.html as string);
        $('.js-email-editor-preview-template').append($(iframeParent));

        const iframeEl = document.getElementById('email-template-preview-iframe') as HTMLIFrameElement;
        if (iframeEl && iframeEl.contentWindow) {
          const doc: Document = iframeEl.contentWindow.document;
          doc.open();
          doc.write(html);
          doc.close();

          doc.querySelectorAll('a[target="_blank"]').forEach((anchor: Element) => {
            const rel = new Set((anchor.getAttribute('rel') ?? '').split(/\s+/).filter(Boolean));
            rel.add('noopener');
            rel.add('noreferrer');
            anchor.setAttribute('rel', Array.from(rel).join(' '));
          });

          window.PMW.initEmailTemplatePreviewBox();

          hideTemplatePreviewLoader();
        }
      } else {
        alert(window.i18next.t('pmwjs_error_occurred_try_again'));
      }
    });
  }

  function hideTemplatePreviewLoader(): void {
    const emailTemplateLoader = $(`#${IDEMAIL_PREVIEW_DIALOG}`).find('.js-template-preview-loader.js-skeleton-loader');
    if (!emailTemplateLoader) {
      return;
    }

    emailTemplateLoader.addClass('_hidden');
  }

  /**
   * sets class for style-manager to show/hide device-specific UI options
   * @param {string} device one of window.PMW.emails.DEVICE_* constants
   */
  function setContainerClassForDevice(device: string): void {
    const deviceClasses = [window.PMW.emails.DEVICE_DESKTOP, window.PMW.emails.DEVICE_TABLET, window.PMW.emails.DEVICE_MOBILE];
    deviceClasses.forEach((deviceClass, i) => {
      deviceClasses[i] = window.PMW.emails.DEVICE_CLASS_PREFIX + deviceClass;
    });

    STYLE_MANAGER_CONTAINER.removeClass(deviceClasses.join(' ')).addClass(window.PMW.emails.DEVICE_CLASS_PREFIX + device.toLowerCase());
  }

  /**
   * @param {string} device one of window.PMW.emails.DEVICE_* constants
   */
  function setActiveDevice(device: string): void {
    const selected = editor.getSelected();
    const isEmailContainerSelected = selected?.is(window.PMW.emails.components.container.PMW_WRAPPER);

    editor.Devices.select(device);
    $(`.js-show-${device}-preview`).addClass('-selected').siblings().removeClass('-selected');

    if (selected && !isEmailContainerSelected) {
      editor.runCommand(CMDS.SCROLL_TO_COMPONENT, {delay: 300});
    }

    editor.runCommand(CMDS.REFRESH_EDITOR, {delay: 10});

    setTimeout(() => {
      fixComponentLoadingOverlaysOnWindowResize();
    }, 400);
  }

  /**
   * @param {object} opts
   * @param {grapesjs.Component} opts.component
   */

  cmds.add(CMDS.OPEN_CROPPER, async (_editorInstance: Editor, _sender: Component, opts: BasicComponentCommandProps) => {
    const {component} = opts;
    if (!component) {
      return;
    }

    const attributes = component.getAttributes() || {};
    const aspectRatio = attributes.width / attributes.height || 1;
    const initialData = window.PMW.emails.props.CROP_DATA as unknown as ImageData;
    const source = initialData ? initialData.source : undefined;

    window.PMW.openCropperModal({
      initialCropData: initialData,
      showCancelButton: false,
      aspectRatio,
      showRemoveBackgroundOption: source ? source !== PMWStockImageSource.PMW_STOCK_IMAGE : undefined,
      getImgSrc: (): string => {
        return (component.get(window.PMW.emails.props.UNCROPPED_SRC) || component.get('src')) as string;
      },
      onApply: (cropResponse: {cropData: ImageData}) => {
        editor.runCommand(CMDS.SET_IMAGE_CROPDATA, {component, cropData: cropResponse.cropData});
      },
    });
  });

  /**
   * @param {object} opts
   * @param {grapesjs.Component} opts.component
   */
  cmds.add(
    CMDS.REMOVE_BACKGROUND,

    async (
      _editorInstance: Editor,
      _sender: Component,
      opts: BasicComponentCommandProps & {
        el: HTMLElement;
      }
    ) => {
      const {component} = opts;
      const {el} = opts;
      if (!component || !el) {
        return;
      }

      if (!isUserLoggedIn()) {
        editor.runCommand(CMDS.AUTHENTICATE_USER, {
          cb: () => {
            editor.runCommand(CMDS.REMOVE_BACKGROUND, {});
          },
        });
        return;
      }
      if (!isUserPremium()) {
        window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_BG_REMOVALS);
        return;
      }

      await doRemoveBackground(editor, component, el);
    }
  );

  cmds.add(CMDS.GET_REMOVE_BG_CREDITS, async () => {
    if (!isUserLoggedIn()) {
      window.PMW.emails.NUM_REMOVE_BACKGROUND_CREDITS = 0;
      return;
    }

    const credits = (await window.PMW.readLocal('emailmaker/getRemoveBackgroundCredits')) as {
      removeBGCredits: number;
    };
    window.PMW.emails.NUM_REMOVE_BACKGROUND_CREDITS = credits.removeBGCredits;
  });

  cmds.add(CMDS.INIT_AUTOSAVE_UI, () => {
    if ($('.js-auto-save-icon').hasClass('_hidden')) {
      $('.navbar-divider').addClass('_hidden');
    } else {
      $('.navbar-divider').removeClass('_hidden');
    }
  });

  cmds.add(CMDS.INIT_AUTOSAVE, () => {
    if (editor.runCommand(window.PMW.emails.commands.IS_USER_OWNER)) {
      editor.runCommand(window.PMW.emails.commands.SET_AUTOSAVE, {autosave: true});
    }
  });

  /**
   * @param {object} opts
   * @param {string} opts.id id for design to open in postermaker
   * @param {number} opts.creatorID design owner's ID to decide whether poster will be edited or copied
   */
  cmds.add(
    CMDS.OPEN_POSTERMAKER,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        id: string;
        creatorID: string;
      }
    ) => {
      if (!isUserLoggedIn()) {
        editor.runCommand(CMDS.AUTHENTICATE_USER, {
          cb: () => {
            editor.runCommand(CMDS.OPEN_POSTERMAKER, opts);
          },
        });
        return;
      }

      if (!window.PMW.plugin.editor.loaded) {
        window.addEventListener('postermaker:load', () => {
          editor.runCommand(CMDS.OPEN_POSTERMAKER, opts);
        });
        return;
      }
      window.addEventListener('message', (e) => {
        const root = `${window.PMW.BASE_URL.split('.com')[0]}.com`;
        if (e.origin === root) {
          addEmbeddedTitle(e);
        }
      });

      const selected = editor.getSelected();

      const designID = opts.id || (selected?.get(window.PMW.emails.props.DESIGN_ID) as number) || 0;
      if (!designID) {
        return;
      }

      const editorAction = window.PMW.emails.DESIGN_COPY;

      const editorOpts: EmbeddedEditorOpenDesignConfig = {
        // eslint-disable-next-line
        // @ts-ignore

        onClose: (savedID: string): void => {},
        elementsToHide: [EmbeddedEditorElementsToHide.FILE_DROPDOWN, EmbeddedEditorElementsToHide.DOWNLOAD_BUTTON],
        onSave: async (savedID: string): Promise<void> => {
          selected?.set(window.PMW.emails.props.DESIGN_ID, savedID);
          await editor.runCommand(window.PMW.emails.commands.POPULATE_MY_DESIGN_PAGE_ID, {component: selected});
          selected?.set(window.PMW.emails.props.DESIGN_MODIFIED, true);
          window.PMW.plugin.editor.close();

          if (savedID) {
            if (editorAction === window.PMW.emails.DESIGN_COPY) {
              selected?.set(window.PMW.emails.props.IS_INTERNAL, true);
              await setPosterInternal(savedID, true);
            }

            await editor.runCommand(CMDS.RELOAD_MODIFIED_DESIGN, {component: selected});
          }
        },
      };

      window.PMW.plugin.editor.openDesign(designID as string, editorAction as EmbeddedEditorAction, editorOpts);
    }
  );

  cmds.add(CMDS.POPULATE_MY_DESIGN_PAGE_ID, async (editorInstance: Editor, _sender: object, opts: BasicComponentCommandProps): Promise<void> => {
    const component = opts.component ?? editorInstance.getSelected();

    if (!component) {
      return;
    }

    const posterId = component.get(window.PMW.emails.props.DESIGN_ID) as string | undefined;
    if (!posterId) {
      return;
    }

    const pageIdsMap = await getPostersPageIds([posterId]);
    const pageIdArray = pageIdsMap[posterId];

    if (!pageIdArray || pageIdArray.length === 0) {
      return;
    }

    const pageId = pageIdArray[0];

    if (!pageId) {
      return;
    }

    component.set(window.PMW.emails.props.DESIGN_PAGE_ID, pageId);
  });

  /**
   * render custom title for embedded design editor
   * @param {Event} e postmessage event indicating UI has been initiated
   */
  function addEmbeddedTitle(e: {data: string}): void {
    if (e.data !== 'INIT_UI') {
      return;
    }

    const iframe = document.getElementById('posterMyWallEditor') as HTMLIFrameElement;
    if (!iframe) {
      return;
    }

    const innerDoc = iframe.contentDocument ? iframe.contentDocument : iframe.contentWindow?.document;

    if (!innerDoc) {
      return;
    }

    const navbar = innerDoc.getElementById('nav');

    if (!navbar) {
      return;
    }

    const logoBtn = navbar.querySelector('.logo.btn-go-home');
    const titleContainer = `<ul class="email-editor-title-container">
                    <span class="email-editor-title visible-desktop body-lg-bold">${window.i18next.t('pmwjs_edit_design')}</span>
            </ul>`;

    if (logoBtn) {
      logoBtn.remove();
      navbar.insertAdjacentHTML('afterbegin', titleContainer);
    }

    window.removeEventListener('message', addEmbeddedTitle);
  }

  async function setPosterInternal(hashedID: string, isInternal: boolean): Promise<void> {
    try {
      await window.PMW.writeLocal('poster/setPosterInternal', {hashedID, isInternal});
    } catch (e) {
      console.error('could not set poster as internal', e);
    }
  }

  cmds.add(CMDS.OPEN_DESIGN_SELECTOR, {
    run(
      editorInstance: Editor,
      _,
      opts: {
        model: Component;
        replaceComponent?: boolean;
        isMultiSelectAllowed?: boolean;
      }
    ) {
      const selected = editorInstance.getSelected(),
        upsertionMethod: UpsertComponentMethod = opts.replaceComponent ? UpsertComponentMethod.REPLACE_WITH : UpsertComponentMethod.APPEND_AFTER;

      if (!isUserLoggedIn()) {
        editorInstance.runCommand(CMDS.AUTHENTICATE_USER, {
          cb: openDesignSelectorLocal.bind(this),
        });
      } else {
        openDesignSelectorLocal();
      }

      function getComponentToAppendComponentTo(selected: Component | undefined, opts: {model?: Component; replaceComponent?: boolean}): Component | undefined {
        if (selected === undefined || typeof opts.model !== 'undefined') {
          return opts.model;
        }

        const isEmailContainerSelected = editor.getSelected()?.is(window.PMW.emails.components.container.PMW_WRAPPER);
        if (!selected || isEmailContainerSelected) {
          const contentContainer = editor.getWrapper()?.findType(window.PMW.emails.components.container.PMW_CONTENT_CONTAINER)?.[0];
          return contentContainer?.getLastChild() ?? undefined;
        } else {
          return selected;
        }
      }

      function openDesignSelectorLocal(): void {
        if (window.PMW.enableMyStuffMultipagesUI) {
          openDesignSelector(
            (pagesData: PosterPageData[]): void => {
              let el = getComponentToAppendComponentTo(selected, opts);

              pagesData.forEach((pageData: PosterPageData) => {
                const insertedComponent = insertDesignComponent(el, upsertionMethod);
                if (!insertedComponent) {
                  console.error('Error while inserting component');
                  return;
                }

                el = insertedComponent;
                void loadDesignPageForID(insertedComponent, {posterHashedId: pageData.posterId, pageHashedId: pageData.pageId} as PosterPageHashedIdInfo);
              });
            },
            finishLoading,
            opts.isMultiSelectAllowed ?? true
          );
          return;
        }

        openDesignSelectorLegacy((id: string): void => {
          const el: Component | undefined = getComponentToAppendComponentTo(selected, opts),
            insertedComponent = insertDesignComponent(el, upsertionMethod);
          if (!insertedComponent) {
            console.error('Error while inserting component');
            return;
          }

          void loadDesignForID(insertedComponent, id);
        }, finishLoading);
      }

      function insertDesignComponent(componentToInsert: Component | undefined, upsertionMethod: UpsertComponentMethod): Component | undefined {
        return editor.runCommand(CMDS.INSERT_COMPONENT, {
          componentToAddTo: componentToInsert,
          componentToAdd: window.PMW.emails.components.graphic.PMW_DESIGN,
          upsertionMethod: upsertionMethod,
        }) as Component | undefined;
      }
    },
  });

  /**
   * fetches design again if it was modified.
   * @param {object} opts
   * @param {grapesjs.Component} opts.component
   */

  cmds.add(CMDS.RELOAD_MODIFIED_DESIGN, async (_editorInstance: Editor, _sender: Component, opts: BasicComponentCommandProps) => {
    const {component} = opts;
    if (!component) {
      return;
    }

    const isModified = component.get(window.PMW.emails.props.DESIGN_MODIFIED) as boolean;
    const designID = component.get(window.PMW.emails.props.DESIGN_ID) as string;
    if (!isModified || !designID) {
      return;
    }

    if (window.PMW.enableMyStuffMultipagesUI) {
      const pageID = component.get(window.PMW.emails.props.DESIGN_PAGE_ID) as string;
      if (pageID) {
        void loadDesignPageForID(component, {posterHashedId: designID, pageHashedId: pageID} as PosterPageHashedIdInfo);
      }
    } else {
      void loadDesignForID(component, designID);
    }
  });

  /**
   * @deprecated Will be removed once multipages are completely enabled. Use {@link loadDesignPageForID}
   */
  async function loadDesignForID(component: Component, id: string): Promise<void> {
    const myDesignComponent = convertImageToDesignComponentIfNeeded(component);

    myDesignComponent.set(window.PMW.emails.props.DESIGN_ID, id);
    editor.runCommand(CMDS.SET_IMAGE_LOADING, {component: myDesignComponent, loading: true});

    try {
      const imgData = (await window.PMW.writeLocal('emailmaker/asyncGetPreviewForDesign', {
        idPoster: id,
      })) as AsyncGetPreviewSuccessResponse;

      myDesignComponent.set(window.PMW.emails.props.DESIGN_CREATOR_ID, imgData.creatorID);
      myDesignComponent.set(window.PMW.emails.props.IS_PREMIUM_DESIGN, imgData.isPremium);
      myDesignComponent.set(window.PMW.emails.props.IS_INTERNAL, imgData.isInternal);

      if (!isPreviewReady(imgData)) {
        pollForPreviewGraphicLegacy(myDesignComponent, id);
      }

      await setDesignSrcData(myDesignComponent, {
        src: imgData.url,
        isPlaceholder: !isPreviewReady(imgData),
      });
    } catch (errorResponse: unknown) {
      editor.runCommand(CMDS.SET_IMAGE_LOADING, {component: myDesignComponent, loading: false});

      if (!errorResponse || typeof errorResponse !== 'object' || !('code' in errorResponse)) {
        openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
        return;
      }

      const responseCode: AsyncGetPreviewErrorCodes | undefined = getEnumMember(AsyncGetPreviewErrorCodes, errorResponse.code as string);
      if (!responseCode) {
        openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
      }

      switch (responseCode) {
        case AsyncGetPreviewErrorCodes.POSTER_NOT_PUBLIC:
          openErrorModal({message: window.i18next.t('pmwjs_design_not_accessible')});
          break;
        case AsyncGetPreviewErrorCodes.POSTER_NOT_FOUND:
          openErrorModal({message: window.i18next.t('pmwjs_emailmaker_unable_to_copy_design')});
          break;
        case AsyncGetPreviewErrorCodes.ERROR:
          openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
          break;
      }
    }
  }

  async function loadDesignPageForID(component: Component, pageData: PosterPageHashedIdInfo): Promise<void> {
    const myDesignComponent = convertImageToDesignComponentIfNeeded(component);

    myDesignComponent.set(window.PMW.emails.props.DESIGN_ID, pageData.posterHashedId);
    myDesignComponent.set(window.PMW.emails.props.DESIGN_PAGE_ID, pageData.pageHashedId);
    editor.runCommand(CMDS.SET_IMAGE_LOADING, {component: myDesignComponent, loading: true});

    try {
      const imgData = (await window.PMW.writeLocal('emailmaker/asyncGetPreviewForDesignV2', {
        posterHashedId: pageData.posterHashedId,
        pageHashedId: pageData.pageHashedId,
      })) as AsyncGetPreviewSuccessResponse;

      myDesignComponent.set(window.PMW.emails.props.DESIGN_CREATOR_ID, imgData.creatorID);
      myDesignComponent.set(window.PMW.emails.props.IS_PREMIUM_DESIGN, imgData.isPremium);
      myDesignComponent.set(window.PMW.emails.props.IS_INTERNAL, imgData.isInternal);

      await setDesignSrcData(myDesignComponent, {
        src: imgData.url,
        isPlaceholder: !isPreviewReady(imgData),
      });

      if (!isPreviewReady(imgData)) {
        pollForPreviewGraphic(myDesignComponent, pageData);
      }
    } catch (errorResponse: unknown) {
      editor.runCommand(CMDS.SET_IMAGE_LOADING, {component: myDesignComponent, loading: false});

      if (!errorResponse || typeof errorResponse !== 'object' || !('code' in errorResponse)) {
        openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
        return;
      }

      const responseCode: AsyncGetPreviewErrorCodes | undefined = getEnumMember(AsyncGetPreviewErrorCodes, errorResponse.code as string);
      if (!responseCode) {
        openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
      }

      switch (responseCode) {
        case AsyncGetPreviewErrorCodes.POSTER_NOT_PUBLIC:
          openErrorModal({message: window.i18next.t('pmwjs_design_not_accessible')});
          break;
        case AsyncGetPreviewErrorCodes.POSTER_NOT_FOUND:
          openErrorModal({message: window.i18next.t('pmwjs_emailmaker_unable_to_copy_design')});
          break;
        case AsyncGetPreviewErrorCodes.ERROR:
          openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
          break;
      }
    }
  }

  function convertImageToDesignComponentIfNeeded(component: Component): Component {
    return convertComponentIfNeeded(editor, component, window.PMW.emails.components.graphic.PMW_IMAGE, window.PMW.emails.blocks.ID_DESIGN);
  }

  function convertDesignToImageComponentIfNeeded(component: Component): Component {
    return convertComponentIfNeeded(editor, component, window.PMW.emails.components.graphic.PMW_DESIGN, window.PMW.emails.blocks.ID_EXPLORE_MEDIA);
  }

  function isPreviewReady(data: AsyncGetPreviewSuccessResponse): boolean {
    return parseInt(data.isReady, 10) === 1;
  }

  /**
   * Starts polling for the preview graphic, to see if it's ready. If it is, it replaces the
   * current preview image with the newly generated GIF preview
   * @deprecated Will be removed once multipages are completely enabled. Use {@link pollForPreviewGraphic}
   */
  function pollForPreviewGraphicLegacy(el: Component, id: string): void {
    window.PMW.pollingAjaxCall(
      () => {
        return window.PMW.write(window.PMW.util.site_url('emailmarketing/preparePreviewGraphic'), {
          designId: id,
          removeWatermark: true,
        });
      },
      () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return window.PMW.getRequest('emailmarketing/checkPreviewGenerationStatus', {
          designId: id,
        });
      },
      (url: string): void => {
        void onPreviewImageReady(el, url);
      },
      (): void => {
        onPreviewGenerationError(el);
      }
    );

    async function onPreviewImageReady(el: Component, url: string): Promise<void> {
      await setDesignSrcData(el, {
        src: url,
        isPlaceholder: false,
      });
    }

    function onPreviewGenerationError(el: Component): void {
      console.error('Error while generating the preview');
      el.set(window.PMW.emails.props.DESIGN_MODIFIED, false);
      editor.runCommand(CMDS.SET_IMAGE_LOADING, {component: el, loading: false});
    }
  }

  /**
   * Starts polling for the preview graphic, to see if it's ready. If it is, it replaces the
   * current preview image with the newly generated GIF preview
   */
  function pollForPreviewGraphic(el: Component, pageData: PosterPageHashedIdInfo): void {
    window.PMW.pollingAjaxCall(
      () => {
        return window.PMW.write(window.PMW.util.site_url('emailmarketing/preparePreviewGraphicV2'), {
          designId: pageData.posterHashedId,
          pageId: pageData.pageHashedId,
          removeWatermark: true,
        });
      },
      () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return window.PMW.getRequest('emailmarketing/checkPreviewGenerationStatusV2', {
          designId: pageData.posterHashedId,
          pageId: pageData.pageHashedId,
        });
      },
      (url: string): void => {
        void onPreviewImageReady(el, url);
      },
      (): void => {
        onPreviewGenerationError(el);
      }
    );

    async function onPreviewImageReady(el: Component, url: string): Promise<void> {
      await setDesignSrcData(el, {
        src: url,
        isPlaceholder: false,
      });
    }

    function onPreviewGenerationError(el: Component): void {
      console.error('Error while generating the preview');
      el.set(window.PMW.emails.props.DESIGN_MODIFIED, false);
      editor.runCommand(CMDS.SET_IMAGE_LOADING, {component: el, loading: false});
    }
  }

  /**
   * set attributes
   * if not a placeholder, disables an image loading state and reselects component to update styles
   * @param {grapesjs.Component} component
   * @param {object} data
   * @param {string} data.src
   * @param {bool} data.isPlaceholder
   */
  async function setDesignSrcData(
    component: Component,
    data: {
      src: string;
      isPlaceholder: boolean;
    }
  ): Promise<void> {
    try {
      editor.runCommand(CMDS.UPDATE_MS_OUTLOOK_STYLES, {component});

      if (data.isPlaceholder) {
        return;
      }

      component.set('src', data.src);
      editor.getModel().skip(() => {
        component.addAttributes({class: component.getClasses() as string[]}, {});
      });
      component.set(window.PMW.emails.props.DESIGN_MODIFIED, false);
      editor.runCommand(CMDS.SET_IMAGE_LOADING, {component, loading: false});
    } catch (e) {
      console.error(e);
    }
  }

  cmds.add(
    CMDS.EMAIL_EXPORTED,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        exportOption: string;
      }
    ) => {
      void window.PMW.write(window.PMW.util.site_url('emailmaker/doRecordEmailExport'), {
        idEmail: window.PMW.idEmailTemplate,
        exportOption: opts.exportOption,
      });
    }
  );

  /**
   * @param {object} opts
   * @param {function} opts.onAddSelected
   */
  cmds.add(
    CMDS.OPEN_MEDIA_MODAL,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        onAddSelected: (items: ImageData[]) => void;
        onStartAddSelected: () => void;
      }
    ) => {
      const videosAllowedCount = window.PMW.emails.MAX_VIDEOS_ALLOWED - editor.runCommand(CMDS.GET_VIDEO_COUNT, {});

      if (videosAllowedCount <= 0) {
        openMessageModal({
          title: window.i18next.t('pmwjs_video_limit'),
          text: window.i18next.t('pmwjs_video_limit_message', {num: window.PMW.emails.MAX_VIDEOS_ALLOWED}),
          onClose: () => {
            openMediaTypeModal({
              mediaType: MediaType.IMAGE,
              onStartAddSelected: opts.onStartAddSelected,
              onAddSelected: (items: ImageData[]) => {
                opts.onAddSelected(items);
              },
              onAddBrandAsset: () => {
                editor.runCommand(CMDS.ON_ADD_BRAND_ASSET, {});
              },
              maxSelectionNumber: 1,
              uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
              videoQualityFiltersDisabled: true,
              hideRecordingOptions: true,
            });
          },
        });
        return;
      }

      openMediaTypeModal({
        onStartAddSelected: opts.onStartAddSelected,
        onAddSelected: (items: ImageData[]) => {
          opts.onAddSelected(items);
        },
        onAddBrandAsset: () => {
          editor.runCommand(CMDS.ON_ADD_BRAND_ASSET, {});
        },
        maxSelectionNumber: 1,
        uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
        videoQualityFiltersDisabled: true,
        hideRecordingOptions: true,
      });
    }
  );

  /**
   * @param {function} opts.onAddSelected
   */
  cmds.add(
    CMDS.OPEN_STICKER_MODAL,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        onAddSelected: (stickers: StickerData[]) => void;
        onStartAddSelected: () => void;
      }
    ) => {
      openStockMediaModal({
        onStartAddSelected: opts.onStartAddSelected,
        tabsToShow: [StockPanelTabs.STICKERS],
        onAddSelected: opts.onAddSelected,
        maxSelectionNumber: 1,
        defaultSearchTab: StockPanelTabs.STICKERS,
        hideOnClose: false,
        panelId: 'STOCK_MEDIA_VIA_EMAIL_EDITOR',
      });
    }
  );

  /**
   * @param {function} opts.onAddSelected
   */
  cmds.add(
    CMDS.OPEN_ICONS_MODAL,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        onAddSelected: (icons: ElementData[]) => void;
        onStartAddSelected: () => void;
      }
    ) => {
      openStockMediaModal({
        onStartAddSelected: opts.onStartAddSelected,
        tabsToShow: [StockPanelTabs.ICONS],
        onAddSelected: opts.onAddSelected,
        maxSelectionNumber: 1,
        defaultSearchTab: StockPanelTabs.ICONS,
        hideOnClose: false,
        panelId: 'STOCK_MEDIA_VIA_EMAIL_EDITOR',
      });
    }
  );

  /**
   * @param {object} opts
   * @param {function} opts.cb
   */
  cmds.add(
    CMDS.ON_ADD_BRAND_ASSET,

    async (
      editorInstance: Editor,
      _sender: Component,
      opts: {
        cb: VoidFunction;
      }
    ): Promise<void> => {
      await editorInstance.runCommand(CMDS.SAVE_TO_CACHE, {});
      if (typeof opts.cb === 'function') {
        opts.cb();
      }
    }
  );

  /**
   * reloads premium backgrounds incase user premium status changed.
   */
  cmds.add(CMDS.RELOAD_PREMIUM_BACKGROUNDS, (editorInstance: Editor): void => {
    editor.getWrapper()?.onAll((component) => {
      const source = component.get(window.PMW.emails.props.CONTENT_SOURCE) as string;
      const style = component.get('style') as Record<string, any> | undefined;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const hasBg = style?.['background-image'] ?? '';
      const hasPremiumContent = source === MULTI_MEDIA_SOURCE_GETTY;

      if (!hasPremiumContent || !hasBg) {
        return;
      }

      editorInstance.runCommand(CMDS.SET_IMAGE_SRC, {
        source,
        isBackground: true,
        component,
        hashedFilename: component.get(window.PMW.emails.props.HASHED_FILENAME),
      });
    });
  });

  /**
   * reloads all MyDesign components to sync any modifications
   */
  cmds.add(CMDS.RELOAD_ALL_DESIGNS, (editorInstance: Editor) => {
    const myDesigns = editorInstance.getWrapper()?.findType(window.PMW.emails.components.graphic.PMW_DESIGN);
    myDesigns?.forEach((myDesign): void => {
      myDesign.set(window.PMW.emails.props.DESIGN_MODIFIED, true);
      editorInstance.runCommand(CMDS.RELOAD_MODIFIED_DESIGN, {component: myDesign});
    });
  });

  cmds.add(CMDS.DUPLICATE_COMPONENT, (editorInstance: Editor) => {
    editorInstance.runCommand(CMDS.CORE_COMPONENT_COPY, {});
    editorInstance.runCommand(CMDS.CORE_COMPONENT_PASTE, {});

    editorInstance.runCommand(CMDS.TRACK_GA_EVENT_COMPONENT, {
      component: editor.getSelected(),
      eventAction: CMDS.DUPLICATE_EVENT_ACTION,
    });
  });

  cmds.add(CMDS.TOGGLE_COMPONENT_LOCK, (editorInstance: Editor, _sender: Component, opts: {el: string}) => {
    const component = editorInstance.getSelected();
    const toggleButton = $(opts.el);
    if (!component) {
      return;
    }

    if (component.get('draggable')) {
      // Tracking temporarily disabled on 8th Nov 2023
      // eventAction = 'EmailMaker_Lock';
      component.set('draggable', false);
      toggleButton.find('.pmw-icon-button-icon').removeClass('icon-lock').addClass('icon-unlock');
      toggleButton.find('.pmw-icon-button-text').html('Unlock');
    } else {
      // Tracking temporarily disabled on 8th Nov 2023
      // eventAction = 'EmailMaker_Unlock';
      component.set('draggable', true);
      toggleButton.find('.pmw-icon-button-icon').removeClass('icon-unlock').addClass('icon-lock');
      toggleButton.find('.pmw-icon-button-text').html('Lock');
    }

    // Tracking temporarily disabled on 8th Nov 2023
    // editor.runCommand(CMDS.TRACK_GA_EVENT_COMPONENT, {
    //     component: component,
    //     eventAction: eventAction
    // });
  });

  cmds.add(
    CMDS.AI_REGENERATE_EMAIL_CAMPAIGN_IMAGES,
    async (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        prompt: string;
        selectedPromptOptionValues: SelectedPromptOptionValues;
        usageSource: EmailCampaignAiRegenerateImageSource;
        contentKey: string;
      }
    ): Promise<number> => {
      const {prompt, selectedPromptOptionValues, usageSource, contentKey} = opts;
      const {imageComponentDataList, allCcids, errors, allComponents} = await gatherImagesData(editor);
      const emailContext = getEmailContextForImageGeneration(editor);
      let totalCostCharged = 0;
      let unresolvedCcids: string[] = [];

      const safelySettle = async (): Promise<number> => {
        let cost = 0;
        try {
          cost = await settleEmailCampaignAiRedesignImageRegeneration(contentKey, prompt, selectedPromptOptionValues, usageSource);
          await refreshTeamCreditsInStore();
        } catch (settleErr) {
          window.PMW.log('AI redesign settle failed', settleErr as Error);
        }
        return cost;
      };

      const clearAllOverlays = (): void => {
        allComponents.forEach((component) => {
          editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: false, component});
        });
      };

      const initiateRegenerationWithOverlayCleanup = async (): Promise<void> => {
        try {
          await initiateAiRedesignImageRegeneration({
            prompt,
            imageComponentDataList,
            emailContext,
            selectedPromptOptionValues,
            usageSource,
            contentKey,
          });
        } catch (err) {
          clearAllOverlays();
          throw err;
        }
      };

      allComponents.forEach((component) => {
        editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: true, component});
      });

      try {
        await initiateRegenerationWithOverlayCleanup();

        const pollResult = await pollAndReplaceGeneratedImages(editor, allCcids, contentKey);
        Object.assign(errors, pollResult.generationErrors);
        unresolvedCcids = pollResult.unresolvedCcids;
      } finally {
        totalCostCharged = await safelySettle();
      }

      if (Object.keys(errors).length > 0 || unresolvedCcids.length > 0) {
        const errorCcids = Object.keys(errors);
        [...errorCcids, ...unresolvedCcids].forEach((ccid) => {
          const component = editor.getWrapper()?.find(`#${ccid}`)[0];
          if (component) {
            editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: false, component});
          }
        });

        throw new AiTextPromptBoxError(`Failed to generate some images`, AiTextErrorTypes.ImageGenerationError);
      }

      return totalCostCharged;
    }
  );

  cmds.add(
    CMDS.AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT,
    async (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        prompt: string;
        selectedPromptOptionValues: SelectedPromptOptionValues;
        usageSource: EmailCampaignAiRewriteSource;
        contentKey: string;
      }
    ): Promise<void> => {
      const {prompt, selectedPromptOptionValues, usageSource, contentKey} = opts;
      const componentsData: TextComponentData[] = getEmailTextWithCcid(editor, usageSource);

      const updatedComponentsData = (await window.PMW.writeLocal('emailcampaignaicontent/rewriteEmailCampaignTextComponentsContent', {
        componentsData,
        selectedPromptOptionValues,
        prompt,
        usageSource,
        contentKey,
      })) as TextComponentData[];

      updatedComponentsData.forEach((component) => {
        const updatedComponent = editor.DomComponents.getWrapper()?.find(`#${component.ccid}`)[0];
        updatedComponent?.components(component.content);
      });

      await refreshTeamCreditsInStore();
    }
  );

  async function morphDesign(designComponent: Component, prompt: string, configOptions: SelectedValueType): Promise<void> {
    try {
      editor.runCommand(CMDS.SET_IMAGE_LOADING, {component: designComponent, loading: true});

      const designId = designComponent.get(window.PMW.emails.props.DESIGN_ID) as string;
      const designPageId = window.PMW.enableMyStuffMultipagesUI ? (designComponent.get(window.PMW.emails.props.DESIGN_PAGE_ID) as string) : null;

      const response = (await window.PMW.readLocal('emailcampaignaicontent/morphDesignPoster', {
        designId,
        designPageId,
        prompt,
        configOptions,
      })) as PosterPageHashedIdInfo;

      if (window.PMW.enableMyStuffMultipagesUI) {
        void loadDesignPageForID(designComponent, response);
      } else {
        void loadDesignForID(designComponent, response.posterHashedId);
      }
    } catch (e) {
      console.error(e);
      editor.runCommand(CMDS.SET_IMAGE_LOADING, {component: designComponent, loading: false});
    }
  }

  cmds.add(
    CMDS.AI_REGENERATE_EMAIL_TEMPLATE_DESIGNS,
    async (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        prompt: string;
        selectedValues: SelectedValueType;
      }
    ): Promise<void> => {
      const {prompt} = opts;
      const configOptions = opts.selectedValues;

      const designComponents = editor.getWrapper()?.find(`[data-pmw-type="${window.PMW.emails.components.graphic.PMW_DESIGN}"]`);

      if (designComponents) {
        const updatePromises = designComponents.map((designComponent) => morphDesign(designComponent, prompt, configOptions));
        await Promise.all(updatePromises);
      }
    }
  );

  /**
   * @param {object} opts
   * @param {string} opts.title   section title
   * @param {string} opts.content HTML markup to add to side panel for display
   */
  cmds.add(
    CMDS.OPEN_SIDE_PANEL,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        title: string;
        content: string | HTMLElement;
        onBack?: () => void;
        hideBackButton?: boolean;
        onClose?: () => void;
      }
    ) => {
      const header = $('.email-editor-mobile-style-header');
      const sidePanel = $('.js-side-panel');
      const backButton = $('.js-nav-back-to-style-list');
      const closeButton = $('.js-side-panel-close');
      const sectorTitle = $('.js-email-editor-responsive-style-title');

      header.removeClass('hidden-desktop');

      if (opts.hideBackButton) {
        backButton.addClass('_hidden').off(window.PMW.click);
      } else {
        backButton.removeClass('_hidden');

        const backButtonCallback =
          opts.onBack ??
          ((title: string): void => {
            editor.runCommand(CMDS.CLOSE_SIDE_PANEL, {
              title,
            });
          }).bind(this, sectorTitle.html());

        backButton.off(window.PMW.click);
        window.PMW.onClick(backButton, null, backButtonCallback);
      }

      closeButton.off(window.PMW.click);
      if (opts.onClose) {
        closeButton.removeClass('_hidden');
        window.PMW.onClick(closeButton, null, opts.onClose);
      } else {
        closeButton.addClass('_hidden');
      }

      sidePanel.parent().scrollTop(0);
      sidePanel.removeClass('-inactive _hidden').addClass('-active').siblings().removeClass('-active').addClass('-inactive _hidden');

      if (opts.title) {
        sectorTitle.html(opts.title);
      }

      if (opts.content) {
        sidePanel.html(opts.content);
      }

      if (editor.getSelected()?.is(window.PMW.emails.components.container.PMW_WRAPPER)) {
        showHeaderForWrapperSidePanel();
      }
    }
  );

  /**
   * @param {object} opts
   * @param {string} opts.title title to display after a closing panel
   */
  cmds.add(
    CMDS.CLOSE_SIDE_PANEL,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        title: string;
      }
    ) => {
      const sidePanel = $('.js-side-panel');
      const backButton = $('.js-nav-back-to-style-list');
      const closeButton = $('.js-side-panel-close');
      const sectorTitle = $('.js-email-editor-responsive-style-title');

      backButton.addClass('_hidden');
      closeButton.addClass('_hidden').off(window.PMW.click);

      if (opts.title) {
        sectorTitle.html(opts.title ?? '');
      }

      sidePanel.parent().scrollTop(0);
      sidePanel.removeClass('-active').addClass('-inactive _hidden').siblings().removeClass('-inactive _hidden').addClass('-active').trigger('focus');

      if (editor.getSelected()?.is(window.PMW.emails.components.container.PMW_WRAPPER)) {
        hideHeaderForWrapperOnSidePanelClose();
      }

      editor.runCommand(window.PMW.emails.commands.TOGGLE_STYLEMANAGER_SCROLL_LISTENER, {toggleOn: true});
    }
  );

  cmds.add(CMDS.INIT_TOOLBAR_AI_REWRITE_DROPDOWN, (editorInstance: Editor, _sender: Component, _opts: {}) => {
    const toolbarEl = editorInstance.Canvas.getToolbarEl();
    if (!toolbarEl) {
      return;
    }

    window.PMW.attachAndGetMutationObserverToElement(
      toolbarEl,
      () => {
        const aiRewriteToolbarDropDownAnchor = document.getElementById(TOOLBAR_AI_REWRITE_DROPDOWN_ANCHOR);

        if (!aiRewriteToolbarDropDownAnchor) return;

        if (aiRewriteToolbarDropDownAnchor.hasChildNodes()) return;

        window.PMW.renderAiRewritePromptBoxDropdown(aiRewriteToolbarDropDownAnchor, {
          editor,
          showPromptBoxOnRender: window.PMW.emails.AI_EDIT_ENABLED,
        });
      },
      {
        childList: true,
        subtree: true,
      }
    );
  });

  cmds.add(
    CMDS.INIT_MERGE_TAGS,
    (
      editorInstance: Editor,
      _sender: Component,
      _opts: {
        component: Component | undefined;
      }
    ) => {
      const toolbarEl = editorInstance.Canvas.getToolbarEl();
      const selectedComponent = editorInstance.getSelected();

      if (!selectedComponent) {
        return;
      }

      const selectedComponentEl = selectedComponent.getEl();

      if (!selectedComponentEl) {
        return;
      }

      hydrateMergeTagsReduxFromComponent(selectedComponent);

      window.PMW.attachAndGetMutationObserverToElement(
        selectedComponentEl,
        (mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'characterData') {
              editorInstance.runCommand(window.PMW.emails.commands.UPDATE_MERGE_TAGS);
            }
          });
        },
        {
          attributes: true,
          childList: true,
          characterData: true,
          subtree: true,
        }
      );

      if (!toolbarEl) {
        return;
      }

      window.PMW.attachAndGetMutationObserverToElement(
        toolbarEl,
        () => {
          const mergeTagsDropDownAnchor = toolbarEl.getElementsByClassName('merge-tag-anchor')[0];

          initMergeTagsDropdown(mergeTagsDropDownAnchor, {
            listId: TOOLBAR_MERGE_TAGS_LIST_ID,
            onSelect: (value) => {
              const currSelectedComponent = editorInstance.getSelected();

              if (!currSelectedComponent) {
                return;
              }

              currSelectedComponent.append(getMergeTagsFromOptionId(value));
              window.PMW.redux.store.dispatch(
                addTagToMap({
                  ccid: currSelectedComponent.ccid,
                  mergeTagKey: value,
                })
              );
              window.PMW.redux.store.dispatch(unSelectedItemsForMultipleLists({listIds: [TOOLBAR_MERGE_TAGS_LIST_ID]}));
            },
          });
        },
        {
          childList: true,
          subtree: true,
        }
      );
    }
  );

  /**
   * @param {object} opts
   * @param {string} opts.type One of 'image' or 'video'
   * @param {number} opts.deltaCount number to increment/decrement getty media count by
   */
  cmds.add(
    CMDS.UPDATE_GETTY_COUNT,
    (
      editorInstance: Editor,
      _sender: Component,
      opts: {
        type: 'image' | 'video';
        deltaCount: number;
      }
    ) => {
      const wrapper = editorInstance.DomComponents.getWrapper();
      const components = editorInstance.getComponents();

      if (components?.models.length) {
        const mainContent = wrapper?.find(`[data-gjs-type="${window.PMW.emails.components.container.PMW_CONTENT}"]`)[0];

        if (mainContent) {
          const property = getGettyCountPropForMediaType(opts.type);
          const newCount = (mainContent.get(property) as number) + opts.deltaCount;

          mainContent.set(property, newCount);
        }
      }
    }
  );

  /**
   * @param {string} [opts.type='image']
   * @param {function} [opts.onClose]
   */
  cmds.add(
    CMDS.SHOW_GETTY_LIMIT_MESSAGE,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        type: 'image' | 'video';
        onClose: VoidFunction;
      }
    ) => {
      window.PMW.openMessageModal({
        title: opts.type === 'image' ? window.i18next.t('pmwjs_getty_limit') : window.i18next.t('pmwjs_getty_video_limit'),
        text:
          opts.type === 'image'
            ? window.i18next.t('pmwjs_getty_limit_message', {limit: MAX_GETTY_IMAGES_PER_EMAIL})
            : window.i18next.t('pmwjs_video_getty_limit_message', {
                num: MAX_GETTY_VIDEOS_PER_EMAIL,
              }),
        onClose: opts.onClose,
      });
    }
  );

  /**
   * @param {object} [opts]
   * @param {grapesjs.Component} [opts.component] scrolls to currently selected if not provided
   * @param {number} [opts.delay=0] scroll after a delay
   * @param {object} [opts.scrollOpts]
   * @param {string} [opts.scrollOpts.behavior="smooth"]
   * @param {string} [opts.scrollOpts.block="center"]
   * @param {string} [opts.scrollOpts.force=true]
   */
  cmds.add(
    CMDS.SCROLL_TO_COMPONENT,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        delay: number;
        component: Component;
        scrollOpts: {
          behavior: string;
          block: string;
          force: boolean;
        };
      }
    ) => {
      const component = opts.component || editor.getSelected();
      const scrollOpts = opts.scrollOpts || {};

      scrollOpts.block = scrollOpts.block || 'center';
      scrollOpts.behavior = scrollOpts.behavior || 'smooth';
      scrollOpts.force = Object.prototype.hasOwnProperty.call(scrollOpts, 'force') ? scrollOpts.force : true;

      setTimeout(() => {
        editor.Canvas.scrollTo(component, scrollOpts);
      }, opts.delay || 0);
    }
  );

  cmds.add(CMDS.SELECT_NEXT_ITEM, (editorInstance: Editor) => {
    const selected = editorInstance.getSelected();
    const collection = selected?.collection;

    if (!collection || collection.length === 0) {
      return;
    }

    const index = (collection.indexOf(selected) + 1) % collection.length;

    const nextModel = collection.at(index);
    if (nextModel) {
      editorInstance.select(nextModel);
    }
  });

  cmds.add(CMDS.SELECT_PREVIOUS_ITEM, () => {
    const selected = editor.getSelected();
    const collection = selected?.collection;

    if (!collection || collection.length === 0) {
      return;
    }

    const index = (collection.indexOf(selected) - 1 + collection.length) % collection.length;

    const previousModel = collection.at(index);
    if (previousModel) {
      editor.select(previousModel);
    }
  });

  cmds.add(CMDS.GET_MAIN_WRAPPER, () => {
    return editor.DomComponents.getWrapper()?.find(`[data-gjs-type="${window.PMW.emails.components.container.PMW_WRAPPER}"]`)[0];
  });

  cmds.add(CMDS.GET_READ_ONLY_FOOTER, () => {
    return editor.DomComponents.getWrapper()?.find(`#${window.PMW.emails.READ_ONLY_FOOTER_ID}`)[0];
  });

  cmds.add(CMDS.GET_EDITABLE_HTML, () => {
    const mainWrapper = editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component;

    const attributesExtractor = (component: Component): Record<string, string> => {
      const attributes: Record<string, string> = {};
      const element = component.getEl();

      if (element) {
        element.getAttributeNames().forEach((attr: string) => {
          attributes[attr] = element.getAttribute(attr) || '';
        });
      }

      return attributes;
    };

    return mainWrapper.toHTML({
      withProps: true,
      attributes: attributesExtractor,
      altQuoteAttr: true,
      keepInlineStyle: true,
    });
  });

  cmds.add(
    CMDS.GET_EDITABLE_COMPONENTS,
    (
      editorInstance: Editor,
      _sender: object,
      opts: {
        component?: Component;
        types?: string[];
        result?: Component[];
      }
    ): Component[] => {
      const {types = [], result = []} = opts;
      const component = opts.component ?? (editorInstance.runCommand(CMDS.GET_MAIN_WRAPPER) as Component);

      const type = component.get('type') ?? '';
      if (types.length === 0 || (type && types.includes(type))) {
        result.push(component);
      }

      component.components().each((model: Component) => {
        return editorInstance.runCommand(CMDS.GET_EDITABLE_COMPONENTS, {
          component: model,
          types,
          result,
        }) as Component[];
      });

      return result;
    }
  );

  cmds.add(CMDS.REPLACE_HTML_CONTENT, (editorInstance: Editor, _: object, opts: {html: string}) => {
    const contentContainer = editorInstance.runCommand(CMDS.GET_MAIN_WRAPPER) as Component;
    const content = editorInstance.runCommand(CMDS.GET_EDITABLE_COMPONENTS) as Component[];

    if (!contentContainer || content.length === 0) {
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(opts.html, 'text/html');

    const elements = doc.querySelectorAll('[id]');

    const elementMap: Record<string, Element> = {};
    elements.forEach((element) => {
      elementMap[element.id] = element;
    });

    const splitStyleIntoKeyValue = (style: string): [string, string] => {
      const parts = style.split(':');
      if (parts.length < 2) {
        return ['', ''];
      }
      const key = parts[0];
      const value = parts.slice(1).join(':');

      return [key.trim(), value.trim()];
    };

    for (const component of content) {
      const element = elementMap[component.getId()];

      if (!element) {
        continue;
      }

      const elementStyles = element.getAttribute('style');
      if (!elementStyles) {
        continue;
      }

      const stylesToApply: StyleProps = {};

      elementStyles.split(';').forEach((style) => {
        if (style) {
          const [key, value] = splitStyleIntoKeyValue(style);
          if (key && value) {
            stylesToApply[key] = value;
          }
        }
      });

      component.setStyle(stylesToApply, {noEvent: true});
    }
  });

  cmds.add(CMDS.UNSELECT_COMPONENT, () => {
    const mainWrapper = editor.runCommand(CMDS.GET_MAIN_WRAPPER) as Component;
    editor.select(mainWrapper);
  });

  cmds.add(CMDS.REFRESH_STYLEMANAGER, () => {
    const selected = editor.getSelected();

    if (!selected) {
      return;
    }

    editor.selectRemove(selected);
    editor.select(selected);
  });

  cmds.add(CMDS.REFRESH_EDITOR, (editor: Editor, opts?: {delay: number}) => {
    if (!opts) {
      editor.refresh();
      return;
    }

    setTimeout(() => {
      editor.refresh();
    }, opts.delay);
  });

  cmds.add(
    CMDS.UPDATE_SECTOR_TITLE,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        title: string;
      }
    ) => {
      $('.js-email-editor-responsive-style-title').html(opts.title);
    }
  );

  cmds.add(
    CMDS.ADD_STYLE_HEADER,
    (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        text: string;
        classes: string;
      }
    ) => {
      return {
        type: window.PMW.emails.styles.ID_STYLE_HEADER,
        text: opts.text,
        property: 'style',
        classes: opts.classes,
      };
    }
  );

  const SHORTCUTS_LIST_GENERAL = [
    {
      label: window.i18next.t('pmwjs_undo'),
      keys: '⌘+z, ctrl+z',
    },
    {
      label: window.i18next.t('pmwjs_redo'),
      keys: '⌘+shift+z, ctrl+shift+z',
    },
    {
      label: window.i18next.t('pmwjs_delete_an_item'),
      keys: 'backspace, delete',
    },
    {
      label: window.i18next.t('pmwjs_copy'),
      keys: '⌘+c, ctrl+c',
    },
    {
      label: window.i18next.t('pmwjs_paste'),
      keys: '⌘+v, ctrl+v',
    },
    {
      label: window.i18next.t('pmwjs_cut'),
      keys: '⌘+x, ctrl+x',
    },
    {
      label: window.i18next.t('pmwjs_duplicate'),
      keys: '⌘+d, ctrl+d',
    },
    {
      label: window.i18next.t('pmwjs_deselection'),
      keys: 'esc',
    },
    {
      label: window.i18next.t('pmwjs_lock_an_item'),
      keys: '⌘+l, ctrl+l',
    },
    {
      label: window.i18next.t('pmwjs_select_next_item'),
      keys: 'tab',
    },
    {
      label: window.i18next.t('pmwjs_select_previous_item'),
      keys: 'shift+tab',
    },
    {
      label: window.i18next.t('pmwjs_select_multiple_items'),
      keys: '⌘+click, ctrl+click',
    },
    {
      label: window.i18next.t('pmwjs_canvas_zoom_in'),
      keys: '+',
    },
    {
      label: window.i18next.t('pmwjs_canvas_zoom_out'),
      keys: '-',
    },
    {
      label: window.i18next.t('pmwjs_canvas_zoom_to_fit'),
      keys: '⌘+0, ctrl+0',
    },
  ];
  const SHORTCUTS_LIST_TEXT = [
    {
      label: window.i18next.t('pmwjs_bold'),
      keys: '⌘+b, ctrl+b',
    },
    {
      label: window.i18next.t('pmwjs_italic'),
      keys: '⌘+i, ctrl+i',
    },
    {
      label: window.i18next.t('pmwjs_underline'),
      keys: '⌘+u, ctrl+u',
    },
    {
      label: window.i18next.t('pmwjs_strikethrough'),
      keys: '⌘+s, ctrl+s',
    },
    {
      label: window.i18next.t('pmwjs_text_left_align'),
      keys: '⌘+shift+l, ctrl+shift+l',
    },
    {
      label: window.i18next.t('pmwjs_text_center_align'),
      keys: '⌘+shift+c, ctrl+shift+c',
    },
    {
      label: window.i18next.t('pmwjs_text_right_align'),
      keys: '⌘+shift+r, ctrl+shift+r',
    },
    {
      label: window.i18next.t('pmwjs_text_justified_align'),
      keys: '⌘+shift+j, ctrl+shift+j',
    },
  ];
  const KEYBOARD_SHORTCUTS_LIST_GENERAL = {
    type: window.PMW.emails.styles.ID_SHORTCUT_LIST,
    list: SHORTCUTS_LIST_GENERAL,
  };
  const KEYBOARD_SHORTCUTS_LIST_TEXT = {
    type: window.PMW.emails.styles.ID_SHORTCUT_LIST,
    list: SHORTCUTS_LIST_TEXT,
  };
  const KEYBOARD_SHORTCUTS_SEARCH = {
    type: 'pmw-input-field',
    property: 'search',
    component: undefined,
    inputContainerClass: 'radius-4',
    inputClass: 'spacing-m-2 spacing-p-0',
    icon: 'search',
    placeholder: window.i18next.t('pmwjs_search_keyboard_shortcut_ellipses'),
    cb: (_selected: Component, searchText: string): void => {
      KEYBOARD_SHORTCUTS_LIST_GENERAL.list = SHORTCUTS_LIST_GENERAL.filter((item) => {
        return item.label.toLowerCase().includes(searchText.toLowerCase());
      });
      KEYBOARD_SHORTCUTS_LIST_TEXT.list = SHORTCUTS_LIST_TEXT.filter((item) => {
        return item.label.toLowerCase().includes(searchText.toLowerCase());
      });

      editor.runCommand(CMDS.SHOW_KEYBOARD_SHORTCUTS);
    },
    eventType: 'input',
  };

  cmds.add(CMDS.SHOW_KEYBOARD_SHORTCUTS, () => {
    const styleManager = editor.StyleManager;
    const searchSector = styleManager.getSector('pmw-search-sector');

    editor.select(undefined);
    editor.runCommand(CMDS.CLOSE_SIDE_PANEL);

    addBackToEditorButton(editor);

    if (!searchSector) {
      updateStyleManangerForKeyboardShortcuts(editor);
    } else {
      styleManager.removeSector('pmw-general-sector');
    }

    const shortcutPanels = getKeyboardShortcutPanels(editor);

    styleManager.addSector(
      'pmw-general-sector',
      {
        name: 'keyboard-shortcuts',
        open: true,
        properties: shortcutPanels,
      },
      {at: 1}
    );
  });

  cmds.add(
    CMDS.ASYNC_GET_SIGNUP_FORMS,
    async (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        callback?: (newItemsStartIndex: number) => void;
      }
    ): Promise<void> => {
      if (!isUserLoggedIn()) {
        return;
      }

      const {limit, offset, signupForms} = window.PMW.emails.SIGNUP_FORMS_DATA;

      const data = (await window.PMW.readLocal('formmaker/getPublishedEmailSignupFormLinkDataByUser', {
        limit,
        offset,
      })) as {signupForms: UserSignupFormLinkData[]};

      window.PMW.emails.SIGNUP_FORMS_DATA = {
        ...window.PMW.emails.SIGNUP_FORMS_DATA,
        offset: offset + data.signupForms.length,
        signupForms: [...signupForms, ...data.signupForms],
      };

      if (opts.callback) {
        opts.callback(offset);
      }
    }
  );

  cmds.add(CMDS.text.ALIGN_LEFT, () => {
    alignSelectedText(editor, 'left');
  });
  cmds.add(CMDS.text.ALIGN_CENTER, () => {
    alignSelectedText(editor, 'center');
  });
  cmds.add(CMDS.text.ALIGN_RIGHT, () => {
    alignSelectedText(editor, 'right');
  });
  cmds.add(CMDS.text.ALIGN_JUSTIFIED, () => {
    alignSelectedText(editor, 'justify');
  });

  cmds.add(
    CMDS.mediaModals.OPEN_ADD_IMAGE_OPTION_SELECTOR,
    (
      _editorInstace: Editor,
      _sender: object,
      opts?: {
        component: Component;
      }
    ) => {
      const component = opts?.component ?? editor.getSelected();

      if (!component) {
        return;
      }

      openMediaTypeModal({
        onStartAddSelected: () => {
          editor.runCommand(window.PMW.emails.commands.SET_IMAGE_LOADING, {
            component: component,
            loading: true,
            text: window.i18next.t('pmwjs_loading'),
          });
        },
        onAddSelected: (imgs: ImageData[]) => {
          stockMediaModelImageSelectionHandler(imgs, component);
        },
        maxSelectionNumber: 1,
        mediaType: MediaType.IMAGE,
        title: window.i18next.t('pmwjs_select_an_option'),
        blacklistedSources: [StockSources.PMW_STOCK_IMAGE],
        hideRecordingOptions: true,
        uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
      });
    }
  );

  cmds.add(
    CMDS.mediaModals.OPEN_MY_UPLOADS_IMAGE,
    (
      _editorInstace: Editor,
      _sender: object,
      opts?: {
        component: Component;
      }
    ) => {
      const component = opts?.component ?? editor.getSelected();

      if (!component) {
        return;
      }

      openUserMediaModal({
        onStartAddSelected: () => {
          editor.runCommand(CMDS.SET_IMAGE_LOADING, {
            component,
            loading: true,
            text: window.i18next.t('pmwjs_loading'),
          });
        },
        onAddSelected: (imgs: ImageData[]) => {
          stockMediaModelImageSelectionHandler(imgs, component);
        },
        maxSelectionNumber: 1,
        defaultTab: UploadsMediaType.IMAGE,
        mediaTypesToShow: [UploadsMediaType.IMAGE],
        uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
        uploadAcceptedMimetype: 'image/*',
        hideFooterSecondaryActions: true,
      });
    }
  );

  cmds.add(
    CMDS.mediaModals.OPEN_EXPLORE_MEDIA_IMAGE,
    (
      _editorInstace: Editor,
      _sender: object,
      opts?: {
        component: Component;
      }
    ) => {
      const component = opts?.component ?? editor.getSelected();

      if (!component) {
        return;
      }

      openStockMediaModal({
        onStartAddSelected: () => {
          editor.runCommand(CMDS.SET_IMAGE_LOADING, {
            component,
            loading: true,
            text: window.i18next.t('pmwjs_loading'),
          });
        },
        onAddSelected: (imgs: ImageData[]) => {
          stockMediaModelImageSelectionHandler(imgs, component);
        },
        maxSelectionNumber: 1,
        tabsToShow: [StockPanelTabs.PHOTOS],
        defaultSearchTab: StockPanelTabs.PHOTOS,
        populateRecentlyAddedOnItemAdd: true,
        showTextToImageTool: true,
      });
    }
  );

  cmds.add(
    CMDS.mediaModals.OPEN_AI_IMAGES,
    (
      _editorInstace: Editor,
      _sender: object,
      opts?: {
        component: Component;
      }
    ) => {
      const component = opts?.component ?? editor.getSelected();
      if (!component) {
        return;
      }

      openStockMediaModal({
        onStartAddSelected: () => {
          editor.runCommand(CMDS.SET_IMAGE_LOADING, {
            component,
            loading: true,
            text: window.i18next.t('pmwjs_loading'),
          });
        },
        onAddSelected: (imgs: ImageData[]) => {
          stockMediaModelImageSelectionHandler(imgs, component);
        },
        maxSelectionNumber: 1,
        tabsToShow: [StockPanelTabs.TEXT_TO_IMAGE],
        defaultSearchTab: StockPanelTabs.TEXT_TO_IMAGE,
        populateRecentlyAddedOnItemAdd: true,
        showTextToImageTool: true,
      });
    }
  );

  cmds.add(
    CMDS.mediaModals.OPEN_ADD_VIDEO_OPTION_SELECTOR,
    (
      _editorInstace: Editor,
      _sender: object,
      opts?: {
        component: Component;
      }
    ) => {
      const component = opts?.component ?? editor.getSelected();

      if (!component) {
        return;
      }

      const videoModalProps = getEmbedVideoModalProps(component);
      const embedVideoProps = {
        showEmbedVideo: true,
        onAddEmbeddedVideo: videoModalProps.onAddVideo,
        onVideoUploadRequest: videoModalProps.onVideoUploadRequest,
        onVideoUploadError: videoModalProps.onVideoUploadError,
        onBack: (): void => {
          editor.runCommand(CMDS.mediaModals.OPEN_EMBED_VIDEO);
        },
      };

      openMediaTypeModal({
        title: window.i18next.t('pmwjs_select_an_option'),
        videoQualityFiltersDisabled: true,
        mediaType: MediaType.VIDEO,
        onAddSelected: (videos: VideoData[]) => {
          stockMediaModelVideoSelectionHandler(videos, component);
        },
        hideRecordingOptions: true,
        maxSelectionNumber: 1,
        uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
        ...embedVideoProps,
      });
    }
  );

  cmds.add(CMDS.mediaModals.OPEN_MY_UPLOADS_VIDEO, () => {
    const component = editor.getSelected();

    if (!component) {
      return;
    }

    openUserMediaModal({
      hideRecordingOptions: true,
      uploadAcceptedMimetype: 'video/*,image/gif',
      onStartAddSelected: () => {
        editor.runCommand(CMDS.SET_IMAGE_LOADING, {
          component,
          loading: true,
          text: window.i18next.t('pmwjs_loading'),
        });
      },
      onAddSelected: (videos: VideoData[]) => {
        stockMediaModelVideoSelectionHandler(videos, component);
      },
      maxSelectionNumber: 1,
      defaultTab: UploadsMediaType.VIDEO,
      mediaTypesToShow: [UploadsMediaType.VIDEO],
      uploadSource: MultiMediaUploadSource.EMAIL_UPLOAD,
      videoQualityFiltersDisabled: true,
    });
  });

  cmds.add(CMDS.mediaModals.OPEN_EXPLORE_MEDIA_VIDEO, () => {
    const component = editor.getSelected();

    if (!component) {
      return;
    }

    openStockMediaModal({
      onStartAddSelected: () => {
        editor.runCommand(window.PMW.emails.commands.SET_IMAGE_LOADING, {
          component,
          loading: true,
          text: window.i18next.t('pmwjs_loading'),
        });
      },
      onAddSelected: (videos: VideoData[]) => {
        stockMediaModelVideoSelectionHandler(videos, component);
      },
      tabsToShow: [StockPanelTabs.VIDEOS],
      defaultSearchTab: StockPanelTabs.VIDEOS,
      maxSelectionNumber: 1,
      populateRecentlyAddedOnItemAdd: true,
      videoQualityFiltersDisabled: true,
    });
  });

  cmds.add(CMDS.mediaModals.OPEN_EMBED_VIDEO, () => {
    const component = editor.getSelected();

    if (!component) {
      return;
    }

    const props = getEmbedVideoModalProps(component);

    openEmbedVideosEmailmakerModal(props);
  });

  cmds.add(CMDS.GET_LOADING_OVERLAY_FOR_EMAIL, (editorInstance: Editor) => {
    const overlay = editorInstance.DomComponents.getWrapper()?.find(`#${window.PMW.emails.EMAIL_OVERLAY_ID}`);

    if (overlay && overlay.length > 0) {
      return overlay[0];
    }

    return null;
  });

  cmds.add(CMDS.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, (editorInstance: Editor, _: object, opts: {show: boolean; style?: EmailOverlayStyle}) => {
    const overlay = editorInstance.runCommand(CMDS.GET_LOADING_OVERLAY_FOR_EMAIL) as Component | null;
    if (opts.show && overlay) {
      return;
    }

    if (!opts.show && !overlay) {
      return;
    }

    editorInstance.UndoManager.stop();

    const overlayClassesMap = {
      [EmailOverlayStyle.LOADING]: '-loading',
      [EmailOverlayStyle.AI_EDIT]: '-ai-edit',
    };

    const overlayClass = opts.style ? overlayClassesMap[opts.style] : overlayClassesMap[EmailOverlayStyle.LOADING];

    const overlayComponent: ComponentDefinition = {
      attributes: {
        id: window.PMW.emails.EMAIL_OVERLAY_ID,
        'data-is-email-overlay': true,
      },
      classes: overlayClass,
      selectable: false,
      hoverable: false,
    };

    if (opts.show) {
      editorInstance.addComponents(overlayComponent, {at: 0});
      editorInstance.runCommand(CMDS.FIX_LOADING_OVERLAY_FOR_EMAIL_SIZE);
    } else {
      const overlay = editorInstance.runCommand(CMDS.GET_LOADING_OVERLAY_FOR_EMAIL) as Component;

      if (overlay) {
        editorInstance.getComponents().remove(overlay);
      }
    }

    editorInstance.UndoManager.start();
  });

  cmds.add(
    CMDS.TOGGLE_MEDIA_COMPONENT_OVERLAY,
    (
      editorInstance: Editor,
      _: object,
      opts: {
        element: HTMLElement;
        show: boolean;
      }
    ) => {
      const mediaImage = opts.element;

      if (!mediaImage) {
        return;
      }

      editorInstance.UndoManager.stop();

      const mediaImageParent = mediaImage.parentNode as HTMLElement;
      const overlayWrapper = mediaImageParent && mediaImageParent.id === window.PMW.emails.EMAIL_MEDIA_OVERLAY_ID ? mediaImageParent : null;

      if (!opts.show) {
        if (overlayWrapper?.parentNode) {
          overlayWrapper.parentNode.insertBefore(mediaImage, overlayWrapper);
          overlayWrapper.parentNode.removeChild(overlayWrapper);
        }

        editorInstance.UndoManager.start();
        return;
      }

      if (!overlayWrapper) {
        const wrapper = document.createElement('div');
        wrapper.id = window.PMW.emails.EMAIL_MEDIA_OVERLAY_ID;
        wrapper.style.position = 'relative';

        mediaImage.parentNode?.insertBefore(wrapper, mediaImage);
        wrapper.appendChild(mediaImage);

        const overlayContent = getMediaComponentOverlayContent(mediaImage);
        wrapper.insertBefore(overlayContent, mediaImage);
      }

      editorInstance.UndoManager.start();
    }
  );

  const getAllComponentLoadingOverlays = (): NodeListOf<HTMLElement> => {
    return editor.Canvas.getBody().querySelectorAll<HTMLElement>(`.${window.PMW.emails.COMPONENT_LOADING_OVERLAY_CLASS}`);
  };

  const getLoadingOverlayIdFromComponent = (component: Component): string => {
    return component.ccid + '-' + window.PMW.emails.COMPONENT_LOADING_OVERLAY_CLASS;
  };

  const getComponentIdFromLoadingOverlayElement = (loadingOverlayEl: HTMLElement): string => {
    return loadingOverlayEl.id.split('-')[0];
  };

  const getStylesForComponentLoadingOverlay = (componentEl: HTMLElement, stylesTargetType: ComponentloadingOverlayStylesTarget): string | ComponentloadingOverlayStyle => {
    const componentWidth = componentEl.clientWidth;
    const marginLeft = calculateComponentOverlayLeftMargin(componentEl);

    switch (stylesTargetType) {
      case ComponentloadingOverlayStylesTarget.COMPONENT_DEFINITION:
        return `
          width: ${componentWidth}px;
          margin-left: ${marginLeft};
          `;
      case ComponentloadingOverlayStylesTarget.ELEMENT:
        return {
          width: `${componentWidth}px`,
          marginLeft: marginLeft,
        };
    }
  };

  const fixComponentLoadingOverlaysOnWindowResize = (): void => {
    const loadingOverlays = getAllComponentLoadingOverlays();
    if (!loadingOverlays) {
      return;
    }

    loadingOverlays.forEach((loadingOverlay) => {
      const componentId = getComponentIdFromLoadingOverlayElement(loadingOverlay);
      const componentEl = editor.Canvas.getBody().querySelector<HTMLElement>(`#${componentId}`);
      const loadingOverlayEl = loadingOverlay;

      if (!componentEl) {
        return;
      }

      if (!loadingOverlayEl) {
        return;
      }

      Object.assign(loadingOverlayEl.style, getStylesForComponentLoadingOverlay(componentEl, ComponentloadingOverlayStylesTarget.ELEMENT));
    });
  };

  cmds.add(
    CMDS.TOGGLE_COMPONENT_LOADING_OVERLAY,
    (
      _editorInstance: Editor,
      _: object,
      opts: {
        show: boolean;
        component: Component;
      }
    ) => {
      const createLoadingOverlay = (overlayId: string): HTMLDivElement => {
        const overlayEl = document.createElement('div');
        overlayEl.id = overlayId;
        overlayEl.classList.add(window.PMW.emails.COMPONENT_LOADING_OVERLAY_CLASS);
        overlayEl.setAttribute('draggable', 'false');
        overlayEl.setAttribute('selectable', 'false');
        overlayEl.setAttribute('hoverable', 'false');
        overlayEl.setAttribute('droppable', 'false');

        return overlayEl;
      };

      const showLoadingOverlayForTableCell = (componentEl: HTMLElement, overlayEl: HTMLDivElement): void => {
        componentEl.style.position = 'relative';
        Object.assign(overlayEl.style, {
          position: 'absolute',
          inset: '0',
        });

        componentEl.insertBefore(overlayEl, componentEl.firstChild);
      };

      const showLoadingOverlayForGeneralCase = (componentEl: HTMLElement, overlayEl: HTMLDivElement): void => {
        const parentAlignment = componentEl.parentElement?.getAttribute('align') ?? null;

        const wrapper = document.createElement('div');
        wrapper.id = window.PMW.emails.COMPONENT_LOADING_OVERLAY_WRAPPER_ID;

        componentEl.parentNode?.insertBefore(wrapper, componentEl);
        wrapper.appendChild(overlayEl);
        wrapper.appendChild(componentEl);
        if (parentAlignment) {
          wrapper.setAttribute('align', parentAlignment);
        }

        wrapper.style.position = 'relative';
        Object.assign(overlayEl.style, {
          position: 'absolute',
          inset: '0',
          'border-radius': componentEl.style.borderRadius,
        });

        Object.assign(overlayEl.style, getStylesForComponentLoadingOverlay(componentEl, ComponentloadingOverlayStylesTarget.ELEMENT));
      };

      const hideLoadingOverlayForTableCell = (componentEl: HTMLElement, loadingOverlayId: string): void => {
        const overlay = componentEl.querySelector(`#${loadingOverlayId}`);
        if (overlay) {
          overlay.remove();
          componentEl.style.position = '';
        }
      };

      const hideLoadingOverlayForGeneralCase = (componentEl: HTMLElement, loadingOverlayId: string): void => {
        const overlayWrapper = componentEl.parentNode as HTMLElement;
        const loadingOverlay = overlayWrapper.firstChild as HTMLElement | null;
        if (overlayWrapper && overlayWrapper.id === window.PMW.emails.COMPONENT_LOADING_OVERLAY_WRAPPER_ID && loadingOverlay?.id === loadingOverlayId) {
          overlayWrapper.parentNode?.insertBefore(componentEl, overlayWrapper);
          overlayWrapper.parentNode?.removeChild(overlayWrapper);
        }
      };

      const component = opts.component;
      const componentEl = component.getEl();
      if (!componentEl) {
        return;
      }

      const OVERLAY_ID = getLoadingOverlayIdFromComponent(component);
      const isTableCell = componentEl.tagName === 'TD';

      if (opts.show) {
        const overlayEl = createLoadingOverlay(OVERLAY_ID);

        if (isTableCell) {
          showLoadingOverlayForTableCell(componentEl, overlayEl);
        } else {
          showLoadingOverlayForGeneralCase(componentEl, overlayEl);
        }
      } else {
        if (isTableCell) {
          hideLoadingOverlayForTableCell(componentEl, OVERLAY_ID);
        } else {
          hideLoadingOverlayForGeneralCase(componentEl, OVERLAY_ID);
        }
      }
    }
  );

  cmds.add(CMDS.REMOVE_COMPONENT_LOADING_OVERLAYS_ON_LOAD, (): void => {
    const loadingOverlays = getAllComponentLoadingOverlays();

    if (!loadingOverlays) {
      return;
    }

    loadingOverlays.forEach((loadingOverlay) => {
      const componentId = getComponentIdFromLoadingOverlayElement(loadingOverlay);
      const component = editor.DomComponents.getWrapper()?.find(`#${componentId}`)[0];

      if (!component) {
        return;
      }

      editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: false, component});
    });
  });

  cmds.add(
    CMDS.APPLY_AUTO_BRANDING_STYLE,

    async (
      editorInstance: Editor,
      _: object,
      opts: {
        colorPalette: ColorPaletteInfo;
        html: string;
        onSuccess?: (editor: Editor, appliedColorPalette: ColorPaletteInfo) => void;
        updateColorPalette?: boolean;
        onUpdatedColorPalette?: (appliedColorPalette: ColorPaletteInfo, suggestedColorPalettes: ColorPaletteInfo[]) => void;
      }
    ) => {
      const {id, colors} = opts.colorPalette;
      const updateAndSuggest = opts.updateColorPalette ?? false;
      const {html} = opts;

      editorInstance.runCommand(CMDS.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: true});
      disableVersionHistoryCta();

      const startTime = Date.now();

      const response = (await window.PMW.writeLocal('autobrandingemailmaker/applyColorMappingToEmail', {
        colors,
        html,
        updateAndSuggest,
      })) as ApplyColorPaletteToEmailServerResponse;

      if (response.colors.length === 0 || !response.html) {
        editorInstance.runCommand(CMDS.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: false});
        enableVersionHistoryCta();
        return;
      }

      const endTime = Date.now();
      const timeDelta = endTime - startTime;
      const waitDuration = Math.max(window.PMW.emails.EMAIL_OVERLAY_ANIMATION_TIME_MS - timeDelta, 0);

      setTimeout(() => {
        editorInstance.runCommand(window.PMW.emails.commands.REPLACE_HTML_CONTENT, {html: response.html});

        if (id && updateAndSuggest && response.suggested && opts.onUpdatedColorPalette) {
          const appliedPalette: ColorPaletteInfo = {
            id,
            colors: response.colors,
            type: ColorPaletteType.BrandKit,
            name: colors.length === response.colors.length ? opts.colorPalette.name : window.i18next.t('pmwjs_suggested'),
          };
          const suggestedPalettes: ColorPaletteInfo[] = response.suggested.map((colorPalette): ColorPaletteInfo => {
            return {
              id,
              colors: colorPalette,
              type: ColorPaletteType.BrandKit,
              name: window.i18next.t('pmwjs_suggested'),
            };
          });

          opts.onUpdatedColorPalette(appliedPalette, suggestedPalettes);
        } else {
          opts.onSuccess?.(editorInstance, opts.colorPalette);
        }

        editorInstance.runCommand(CMDS.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: false});
        enableVersionHistoryCta();
      }, waitDuration);
    }
  );

  cmds.add(CMDS.ASYNC_FETCH_ALL_COLOR_PALETTES, (): void => {
    window.PMW.readLocal('colorpalettes/getAdminColorPalettes', {})
      .then((response: ColorPaletteInfo[]) => {
        window.PMW.emails.COLOR_PALETTES = response;
      })
      .catch((error: Error) => {
        console.error('Error fetching color palettes', error);
      });
  });

  cmds.add(CMDS.ASYNC_FETCH_ALL_COLOR_PALETTE_PILLS, (): void => {
    window.PMW.readLocal('colorpalettes/getColorPalettePillsToShow', {})
      .then((response: ColorPalettePill[]) => {
        window.PMW.emails.COLOR_PALETTE_PILLS = response;
      })
      .catch((error: Error) => {
        console.error('Error fetching color palette pills', error);
      });
  });

  cmds.add(CMDS.FETCH_SOCIAL_LINK_SUGGESTION, async (): Promise<void> => {
    if (!isUserLoggedIn()) {
      return;
    }

    try {
      const socialLinkSuggestions = (await window.PMW.writeLocal('emailmaker/getSocialLinksSuggestions', {})) as SocialLinkSuggestions;
      window.PMW.redux.store.dispatch(updateSocialLinkSuggestions(socialLinkSuggestions));
    } catch (error) {
      console.error('Error fetching social link suggestions', error);
    }
  });

  cmds.add(CMDS.GET_ALL_COLORS_HEXES_IN_COMPONENT, (editorInstance: Editor, _: object, opts: BasicComponentCommandProps): string[] => {
    if (!opts.component) {
      return [];
    }

    const colors: string[] = [];
    const style = opts.component.get('style') as StyleProps;
    const color = style.color as string;
    const bgColor = style['background-color'] as string;
    const borderColor = style['border-color'] as string;
    const values: string[] = [color, bgColor, borderColor];

    values.map((value): number | null => {
      if (value) {
        return colors.push(colorToHex(value));
      }
      return null;
    });

    opts.component.components()?.map((child): number => {
      const colorsInChildren = editorInstance.runCommand(CMDS.GET_ALL_COLORS_HEXES_IN_COMPONENT, {component: child}) as string[];
      return colors.push(...colorsInChildren);
    });

    return colors;
  });

  cmds.add(CMDS.REMOVE_PARAMS_FROM_URL, {
    run() {
      const paramsToRemove = [OnLoadUrlParamsConfig.AutoUpdateEmailContentParams];
      const url = new URL(window.location.href);
      const allParams = url.searchParams;

      paramsToRemove.forEach((param) => {
        allParams.delete(param);
      });

      const newUrl = `${url.origin}${url.pathname}${allParams.toString() ? '?' + allParams.toString() : ''}`;
      window.history.replaceState(null, '', newUrl);
    },
  });

  cmds.add(CMDS.GET_AUTO_UPDATES, {
    run() {
      const autoUpdates = JSON.parse($('#html-editor-container').attr('data-auto-update-email-content-params')!) as AutoUpdateEmailContentParams;

      if (autoUpdates.autoUpdateTextContentParams) {
        autoUpdates.autoUpdateTextContentParams.rewriteContentPromptOptions = formatPromptOptions(autoUpdates.autoUpdateTextContentParams.rewriteContentPromptOptions);
      }

      window.PMW.emails.AUTO_UPDATE_EMAIL_CONTENT_PARAMS = autoUpdates;
    },
  });

  cmds.add(CMDS.APPLY_AUTO_UPDATES, {
    async run() {
      const autoUpdateEmailContentParams = window.PMW.emails.AUTO_UPDATE_EMAIL_CONTENT_PARAMS;

      if (autoUpdateEmailContentParams?.autoUpdateEmailTemplateOnLoad) {
        await rewriteTextContentOnLoad(autoUpdateEmailContentParams);
        await applyAutoBrandingOnLoad(autoUpdateEmailContentParams);
      }
    },
  });

  cmds.add(CMDS.GET_PUPPETEER_CONFIG, {
    run() {
      const puppeteerConfigString = $('#html-editor-container').attr('data-editor-puppeteer-config');
      if (!puppeteerConfigString) {
        window.PMW.emails.PUPPETEER_CONFIG = null;
        return;
      }

      const puppeteerConfig = JSON.parse(puppeteerConfigString) as EmailMakerPuppeteerConfig;

      if (!puppeteerConfig) {
        window.PMW.emails.PUPPETEER_CONFIG = null;
        return;
      }

      window.PMW.emails.PUPPETEER_CONFIG = puppeteerConfig;
    },
  });

  cmds.add(CMDS.ADD_PUPPETEER_LISTENERS, {
    run() {
      const puppeteerConfig = window.PMW.emails.PUPPETEER_CONFIG;
      if (!puppeteerConfig) {
        return;
      }

      if (puppeteerConfig.flow === EmailMakerPuppeteerFlow.TEMPLATE_AUTO_BRANDING) {
        window.addEventListener(PALETTE_APPLIED_EVENT, notifyPuppeteerPostAutoBranding);
      }
    },
  });

  cmds.add(CMDS.FIX_VERTICAL_ALIGNMENT_VISUAL, (editorInstance: Editor) => {
    const tableCells = editorInstance.getWrapper()?.find(`[data-pmw-type="${window.PMW.emails.components.table.PMW_CELL}"]`);

    tableCells?.forEach((cell) => {
      const isTableParent = cell.parent()?.parent()?.is(window.PMW.emails.components.table.PMW_TABLE);

      if (isTableParent) {
        return;
      }

      const cellEl = cell.getEl();
      const grapesjsVerticalAlign = cell.getStyle()['vertical-align'] as string;
      const domVerticalAlign = cellEl?.style.verticalAlign;

      if (cellEl && grapesjsVerticalAlign && domVerticalAlign !== grapesjsVerticalAlign) {
        cellEl.style.verticalAlign = grapesjsVerticalAlign;
      }
    });
  });

  cmds.add(CMDS.GET_ALL_COMPONENTS_BY_TYPE, (editorInstance: Editor, _sender: object, opts: {types: string[]} & BasicComponentCommandProps): Component[] => {
    const parent = opts.component ?? (editorInstance.runCommand(CMDS.GET_MAIN_WRAPPER) as Component);

    const result: Component[] = [];

    for (const child of parent.components().models) {
      const type = child.get('type');
      if (type && opts.types.includes(type)) {
        result.push(child);
      } else {
        const childResults = editorInstance.runCommand(CMDS.GET_ALL_COMPONENTS_BY_TYPE, {types: opts.types, component: child}) as Component[];
        result.push(...childResults);
      }
    }

    return result;
  });

  cmds.add(CMDS.TOGGLE_AI_EDIT, (editorInstance: Editor, _sender: object, opts: {enable: boolean}): void => {
    const editableComponentTypes = [window.PMW.emails.components.text.PMW_TEXT, window.PMW.emails.components.text.PMW_BUTTON, window.PMW.emails.components.graphic.PMW_IMAGE];

    const allImageAndTextComponents = editorInstance.runCommand(CMDS.GET_ALL_COMPONENTS_BY_TYPE, {types: editableComponentTypes}) as Component[];

    if (opts.enable) {
      editorInstance.runCommand(CMDS.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: true, style: EmailOverlayStyle.AI_EDIT});
      editorInstance.runCommand(CMDS.UNSELECT_COMPONENT);
      editorInstance.Canvas.getFrameEl().contentDocument?.querySelector('.gjs-dashed')?.classList.add('ai-edit');

      setTimeout(() => {
        if (window.PMW.emails.AI_EDIT_ENABLED) {
          editorInstance.getModel().skip(() => {
            allImageAndTextComponents.forEach((component) => {
              component.addClass('-highlighted-for-ai-edit');
            });
          });
        }

        editorInstance.runCommand(CMDS.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: false});
      }, 2250);
    } else {
      editorInstance.Canvas.getFrameEl().contentDocument?.querySelector('.gjs-dashed')?.classList.remove('ai-edit');
      editorInstance.getModel().skip(() => {
        allImageAndTextComponents.forEach((component) => {
          component.removeClass('-highlighted-for-ai-edit');
        });
      });
    }

    window.PMW.emails.AI_EDIT_ENABLED = opts.enable;
  });

  cmds.add(
    CMDS.AI_REDESIGN_EMAIL_CAMPAIGN,
    async (
      _editorInstance: Editor,
      _sender: Component,
      opts: {
        prompt: string;
        selectedPromptOptionValues: SelectedPromptOptionValues;
        editableComponentsMap: EditableComponentsMap;
        usageSource: EmailCampaignAiRedesignSource;
      }
    ): Promise<void> => {
      const {prompt, selectedPromptOptionValues, editableComponentsMap, usageSource} = opts;
      const contentKey = getUniqueId();
      let error: ErrorType | undefined = undefined;
      let totalCharged = 0;

      try {
        initiatePosterMorphLoading(EMAIL_MAKER_AI_REDESIGN_LOADING_TOAST, 'pmwjs_redesigning_with_ai_ellipsis');
        window.PMW.redux.store.dispatch(setIsLoading(true));
        disableVersionHistoryCta();
        window.PMW.redux.store.dispatch(setLastRunCreditsCharged(undefined));

        totalCharged = await applyAiRedesignChanges(editor, editableComponentsMap, prompt, selectedPromptOptionValues, contentKey);
        window.PMW.redux.store.dispatch(setLastRunCreditsCharged(totalCharged));
      } catch (e) {
        error = e as ErrorType;
        handleAiRedesignError(error);
      } finally {
        const redesignHashedId = await saveAiRedesignGenerationResults(prompt, usageSource, contentKey);

        window.PMW.redux.store.dispatch(setIsLoading(false));
        enableVersionHistoryCta();
        terminatePosterMorphLoading(EMAIL_MAKER_AI_REDESIGN_LOADING_TOAST);

        if (redesignHashedId && !error) {
          const creditUsage = isMobileWidth() ? {creditsCharged: totalCharged, remainingCredits: (await getTeamCredits()).credits} : undefined;
          handleAiRedesignFeedback(redesignHashedId, creditUsage);
        }
      }
    }
  );

  cmds.add(CMDS.FETCH_AI_REWRITE_RECENT_PROMPT, async (): Promise<void> => {
    await fetchAiRewriteRecentPrompt();
  });

  cmds.add(CMDS.INSERT_COMPONENT, {
    run: function (
      editorInstance: Editor,
      _: Component,
      opts: {componentToAddTo?: Component; componentToAdd: string; upsertionMethod: UpsertComponentMethod}
    ): Component | undefined {
      const {componentToAddTo, upsertionMethod} = getComponentToAddTo(opts.componentToAddTo, opts.upsertionMethod);
      if (!componentToAddTo) {
        console.error('Unable to get component to insert/append to');
        return undefined;
      }

      if (upsertionMethod === UpsertComponentMethod.REPLACE_WITH) {
        return componentToAddTo.replaceWith({type: opts.componentToAdd})[0] ?? undefined;
      }

      const componentToInsertIn = getComponentToInsertInto(componentToAddTo, opts.componentToAdd, upsertionMethod);
      if (!componentToInsertIn) {
        console.error('Unable to get component parent for appendment/insertion');
        return undefined;
      }

      const indextoInsertIn = getInsertionIndex(componentToAddTo, componentToInsertIn, upsertionMethod);

      const insertedComponent: Component | Component[] = componentToInsertIn.components().add({type: opts.componentToAdd}, {at: indextoInsertIn});
      return (Array.isArray(insertedComponent) ? (insertedComponent[indextoInsertIn] ?? undefined) : insertedComponent) as Component | undefined;

      function getComponentToAddTo(
        componentToAddTo: Component | undefined,
        upsertionMethod: UpsertComponentMethod
      ): {componentToAddTo: Component | undefined; upsertionMethod: UpsertComponentMethod} {
        if (componentToAddTo) {
          return {componentToAddTo, upsertionMethod};
        }

        if (upsertionMethod === UpsertComponentMethod.REPLACE_WITH) {
          console.error('Replacement requires that the component to be replaced with is passed');
          return {componentToAddTo: undefined, upsertionMethod};
        }

        const editorSelected = editorInstance.getSelected(),
          isNoComponentSelected = editorSelected ? editorSelected?.is(window.PMW.emails.components.container.PMW_WRAPPER) : false,
          editorContentContainer = editorInstance.getWrapper()?.findType(window.PMW.emails.components.container.PMW_CONTENT_CONTAINER)?.[0];

        if (isNoComponentSelected || !editorSelected) {
          return {componentToAddTo: editorContentContainer, upsertionMethod: UpsertComponentMethod.INSERT_INTO};
        }

        return {componentToAddTo: editorSelected, upsertionMethod};
      }

      function getComponentToInsertInto(componentToAddTo: Component, componentToAdd: string, upsertionMethod: UpsertComponentMethod): Component | undefined {
        let componentToInsertIn: Component | undefined = undefined;
        if (upsertionMethod === UpsertComponentMethod.APPEND_AFTER || upsertionMethod === UpsertComponentMethod.PREPEND_BEFORE) {
          componentToInsertIn = componentToAddTo.parent();
        } else {
          componentToInsertIn = componentToAddTo;
        }

        let canMoveResult: CanMoveResult | undefined;

        while (true) {
          if (!componentToInsertIn) {
            return undefined;
          }

          canMoveResult = editor.Components.canMove(componentToInsertIn, componentToAdd);
          if (canMoveResult.result) {
            break;
          }

          componentToInsertIn = componentToInsertIn.parent();
        }

        if (!canMoveResult?.result) {
          return undefined;
        }

        return componentToInsertIn;
      }

      function getInsertionIndex(componentToAddTo: Component, componentToInsertIn: Component, upsertionMethod: UpsertComponentMethod): number {
        if (upsertionMethod === UpsertComponentMethod.APPEND_AFTER) {
          return (componentToAddTo.index() ?? -1) + 1;
        }

        if (upsertionMethod === UpsertComponentMethod.PREPEND_BEFORE) {
          return (componentToAddTo.index() ?? 1) - 1;
        }

        const lastChild = componentToInsertIn.getLastChild();
        return (lastChild?.index() ?? -1) + 1;
      }
    },
  });

  function notifyPuppeteerPostAutoBranding(): void {
    const htmlContent = editor.runCommand(CMDS.GET_INLINED_HTML, {}) as string;
    const jsonContent = JSON.stringify(editor.getProjectData());

    void window.PMW.writeLocal('emailmaker/saveTemplateDataForPuppeteerAutoBranding', {
      htmlContent: htmlContent,
      jsonContent: jsonContent,
      idEmailTemplate: window.PMW.idEmailTemplate,
    });

    notifyPuppeteer(PUPPETEER_NOTIFIER_ID);
  }

  async function canBypassOwnershipChecks(): Promise<boolean> {
    const canUseBypassedEmail = (await window.PMW.readLocal('emailmaker/canUseBypassedEmail', {
      idEmailTemplate: window.PMW.idEmailTemplate,
    })) as CanUseBypassedEmailResponse;

    if (!canUseBypassedEmail) {
      return false;
    }

    return canUseBypassedEmail.canUseEmail;
  }

  // SelfCodeReviewZain: will be converted to redesignOnLoad
  async function rewriteTextContentOnLoad(autoUpdateEmailContentParams: AutoUpdateEmailContentParams): Promise<void> {
    const autoUpdateTextContentParams = autoUpdateEmailContentParams.autoUpdateTextContentParams;

    if (!autoUpdateTextContentParams) {
      return;
    }

    const canBypassOwnership = await canBypassOwnershipChecks();

    if (!isUserPremium() && !canBypassOwnership) {
      await window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT);
      return;
    }
    initiatePosterMorphLoading('updatingTemplateText');

    editor.runCommand(window.PMW.emails.commands.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: true});
    try {
      await editor.runCommand(window.PMW.emails.commands.AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT, {
        prompt: autoUpdateTextContentParams.rewriteContentPrompt,
        selectedPromptOptionValues: {
          [PromptOption.MOOD]: RedesignMood.Formal,
          [PromptOption.EMOJI_USAGE]: EmojiUsage.Disabled,
        },
        usageSource: EmailCampaignAiRewriteSource.EMAIL_MAKER_AI_REDESIGN,
        contentKey: getUniqueId(),
      });
    } catch (e) {
      console.error(e);
      // SelfCodeReviewZain: need to show error toast here once implemented
    }

    editor.runCommand(window.PMW.emails.commands.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: false});

    await editor.runCommand(window.PMW.emails.commands.AI_REGENERATE_EMAIL_CAMPAIGN_IMAGES, {
      prompt: autoUpdateTextContentParams.rewriteContentPrompt,
      selectedPromptOptionValues: {
        [PromptOption.MOOD]: RedesignMood.Formal,
        [PromptOption.AI_MODEL]: AiModel.Flux,
      },
      usageSource: EmailCampaignAiRewriteSource.EMAIL_MAKER_AI_REDESIGN,
      contentKey: getUniqueId(),
    });
    terminatePosterMorphLoading('updatingTemplateText');
  }

  async function applyAutoBrandingOnLoad(autoUpdateEmailContentParams: AutoUpdateEmailContentParams): Promise<void> {
    const autoUpdateBrandingParams = autoUpdateEmailContentParams.autoUpdateBrandingParams;
    const waitDuration = Math.max(window.PMW.emails.EMAIL_OVERLAY_ANIMATION_TIME_MS, 0);

    if (!autoUpdateBrandingParams) {
      return;
    }

    initiatePosterMorphLoading('updatingTemplateStyles');

    const initialHtml = editor.runCommand(window.PMW.emails.commands.GET_EDITABLE_HTML) as string;
    const colorPalette: ColorPaletteInfo = {
      colors: autoUpdateBrandingParams.colors,
    };

    await editor.runCommand(window.PMW.emails.commands.APPLY_AUTO_BRANDING_STYLE, {
      colorPalette,
      html: initialHtml,
      onSuccess: storeAppliedPaletteInHtml,
    });

    setTimeout(() => {
      editor.runCommand(window.PMW.emails.commands.REFRESH_STYLEMANAGER);
      terminatePosterMorphLoading('updatingTemplateStyles');
    }, waitDuration);
  }

  function formatPromptOptions(promptOptions: SelectedValueType): SelectedValueType {
    return {
      [PromptBoxConfigOptionType.EMAIL_CAMPAIGN_SUBJECT_TONE]: promptOptions.tone,
      [PromptBoxConfigOptionType.PURPOSE]: promptOptions.purpose,
      [PromptBoxConfigOptionType.EMOJIS]: promptOptions.useEmojis,
    };
  }

  function formatURL(value: string): string {
    let newValue: string = value;
    if (!/^[a-zA-Z]+:\/\//.exec(value)) {
      newValue = HTTP_PROTOCOL + value;
    }

    return newValue;
  }

  function doesInputStartWithMailto(value: string): boolean {
    const newValue = value.toLowerCase();

    return newValue.startsWith(MAILTO_HREF);
  }

  /**
   *
   * @param value
   * @returns {string}
   */
  function formatEmail(value: string): string {
    let newValue = value.toLowerCase();

    if (!newValue.startsWith(MAILTO_HREF)) {
      newValue = MAILTO_HREF + newValue;
    }

    return newValue;
  }

  /**
   *
   * @param value
   * @returns {string}
   */
  function formatTelephoneNum(value: string): string {
    let newValue = value.toLowerCase();

    if (!newValue.startsWith('t')) {
      newValue = TELEPHONE_HREF + newValue;
    }

    return newValue;
  }

  /**
   * Checks if the given string is a URL or not.
   * @param str
   * @returns {boolean}
   */
  function isURL(str: string): boolean {
    const lowerStr = str.toLowerCase();

    const regexp = /^(?:(?:https?|ftp):\/\/)?(?:[^\s:@/?#]+(?::[^\s@/?#]*)?@)?(?:localhost|\d{1,3}(?:\.\d{1,3}){3}|\[[0-9a-f:]+\]|(?:[a-z\d\u00a1-\uffff_-]+\.)+[a-z\u00a1-\uffff]{2,})(?::\d+)?(?:[/?#]\S*)?$/i;
    return regexp.test(lowerStr);
  }

  /**
   *
   * @param str
   * @returns {boolean}
   */
  function isPhoneNumber(str: string): boolean {
    const sanitizedStr = str.replace(/\s/g, '');
    const regexp = /[\+]?[(]?[0-9]+[)]?/gi;
    return regexp.test(sanitizedStr);
  }

  /**
   * Checks if the given component has text.
   *
   * @param {Component} component
   * @return {boolean}
   */
  function componentHasText(component: Component): boolean {
    return (
      component.is(window.PMW.emails.components.text.PMW_TEXT) ||
      component.is(window.PMW.emails.components.text.PMW_BUTTON) ||
      component.is(window.PMW.emails.components.social.PMW_SOCIALS)
    );
  }

  /**
   * Aligns the selected text in the given editor to the specified alignment.
   *
   * @param {grapesjs.Editor} grapesjsEditor
   * @param {string} alignment
   */
  function alignSelectedText(grapesjsEditor: Editor, alignment: string): void {
    const selected = editor.getSelected();
    if (selected && componentHasText(selected)) {
      grapesjsEditor.runCommand(CMDS.SET_COMPONENT_STYLE, {property: 'text-align', value: alignment});
    }
  }

  /**
   * @param {grapesjs.Editor} grapesjsEditor
   * @return {Object[]}
   */
  function getKeyboardShortcutPanels(grapesjsEditor: Editor): any[] {
    const shortcutPanels = [];

    if (KEYBOARD_SHORTCUTS_LIST_GENERAL.list.length > 0) {
      shortcutPanels.push(
        grapesjsEditor.runCommand(CMDS.ADD_STYLE_HEADER, {
          text: window.i18next.t('pmwjs_general'),
          classes: 'body-md-bold',
        })
      );
      shortcutPanels.push(KEYBOARD_SHORTCUTS_LIST_GENERAL);
    }
    if (KEYBOARD_SHORTCUTS_LIST_TEXT.list.length > 0) {
      shortcutPanels.push(
        grapesjsEditor.runCommand(CMDS.ADD_STYLE_HEADER, {
          text: window.i18next.t('pmwjs_text'),
          classes: 'body-md-bold',
        })
      );
      shortcutPanels.push(KEYBOARD_SHORTCUTS_LIST_TEXT);
    }

    return shortcutPanels;
  }

  /**
   * Updates the style manager panel to display the keyboard shortcuts
   * @param {grapesjs.Editor} grapesjsEditor
   */
  function updateStyleManangerForKeyboardShortcuts(grapesjsEditor: Editor): void {
    grapesjsEditor.runCommand(CMDS.UPDATE_SECTOR_TITLE, {
      title: window.i18next.t('pmwjs_keyboard_shortcuts'),
      icon: '',
    });
    const styleManager = grapesjsEditor.StyleManager;

    styleManager.getSectors().reset();
    styleManager.addSector(
      'pmw-search-sector',
      {
        name: 'keyboard-shortcuts-search',
        open: true,
        properties: [KEYBOARD_SHORTCUTS_SEARCH],
      },
      {at: 0}
    );

    KEYBOARD_SHORTCUTS_LIST_GENERAL.list = SHORTCUTS_LIST_GENERAL;
    KEYBOARD_SHORTCUTS_LIST_TEXT.list = SHORTCUTS_LIST_TEXT;
  }

  /**
   * Creates a back button to return to the email styles
   *
   * @param {grapesjs.Editor} grapesjsEditor
   */
  function addBackToEditorButton(grapesjsEditor: Editor): void {
    const backButton = $('.js-nav-back-to-style-list');
    backButton.removeClass('_hidden');
    backButton.one('click', () => {
      grapesjsEditor.runCommand(CMDS.UNSELECT_COMPONENT);
    });
  }

  /**
   * Runs the relevant commands to update the image source to the media selected in the modal
   * @param imgs
   * @param component
   */
  function stockMediaModelImageSelectionHandler(imgs: ImageData[], component: Component): void {
    if (imgs.length === 0) {
      return;
    }

    const imageComponent = convertDesignToImageComponentIfNeeded(component);

    if (imgs[0].source === UserImageSource.EMAIL_UPLOAD || imgs[0].source === UserImageSource.UPLOAD) {
      editor.runCommand(window.PMW.emails.commands.TRACK_GA_EVENT_COMPONENT, {
        component: imageComponent,
        eventAction: GA4EventName.EMAILMAKER_UPLOAD_MEDIA,
      });
    }

    editor.runCommand(window.PMW.emails.commands.SET_IMAGE_SRC, {
      component: imageComponent,
      source: imgs[0].source,
      extension: imgs[0].extension,
      hashedFilename: imgs[0].hashedFilename,
    });
  }

  /**
   * Runs the relevant commands to update the video source to the media selected in the modal
   *
   * @param videos
   * @param component
   */
  function stockMediaModelVideoSelectionHandler(videos: VideoData[], component: Component): void {
    if (videos.length === 0) {
      return;
    }

    component.removeAttributes(['data-url']);
    component.addAttributes({'data-is-pmw-embedded-video': 0});
    if (videos[0].source === UserVideoSource.EMAIL_UPLOAD || videos[0].source === UserVideoSource.UPLOAD) {
      editor.runCommand(window.PMW.emails.commands.TRACK_GA_EVENT_COMPONENT, {
        component: component,
        eventAction: GA4EventName.EMAILMAKER_UPLOAD_MEDIA,
      });
    }

    editor.runCommand(window.PMW.emails.commands.REMOVE_COMPONENT_ANCHOR, {
      component: component,
    });

    editor.runCommand(window.PMW.emails.commands.SET_IMAGE_SRC, {
      component: component,
      source: videos[0].source,
      extension: videos[0].extension,
      hashedFilename: videos[0].hashedFilename,
    });
  }

  /**
   * Get shared props for embed a video modal
   * @param component
   * @returns {EmbedVideosEmailmakerInputModalProps}
   */
  function getEmbedVideoModalProps(component: Component): EmbedVideosEmailmakerInputModalProps {
    return {
      onAddVideo: (videoURL: string, thumbnailURL: string): void => {
        editor.runCommand(window.PMW.emails.commands.SET_EMBEDDED_VIDEO_SRC, {
          component: component,
          extension: 'jpg',
          thumbnailURL,
          videoURL,
        });
      },
      onVideoUploadRequest: (): void => {
        editor.runCommand(window.PMW.emails.commands.SET_IMAGE_LOADING, {
          component: component,
          loading: true,
          text: window.i18next.t('pmwjs_loading'),
        });
      },
      onVideoUploadError: (errorMessage: string): void => {
        window.PMW.openErrorModal({message: errorMessage});
        editor.runCommand(window.PMW.emails.commands.SET_IMAGE_LOADING, {
          component: component,
          loading: false,
        });
      },
      onBack: noop,
    };
  }

  function calculateElementOverlayLeftMargin(element: HTMLElement): string {
    const computedStyle = window.getComputedStyle(element);
    return computedStyle.marginLeft;
  }

  function calculateMediaOverlayLeftMargin(mediaImage: HTMLElement): string {
    const parentAlignment = mediaImage.parentElement?.getAttribute('align') ?? '';
    const width = getElementResponsiveWidth(mediaImage);
    const percentageWidth = parseFloat(width);

    let marginLeft = calculateElementOverlayLeftMargin(mediaImage);

    if (isNaN(percentageWidth) || percentageWidth <= 0) {
      marginLeft = `${parseInt(marginLeft) * (1 / window.PMW.emails.ZOOM_VALUE)}px`;
      return marginLeft;
    }

    const mediaRect = mediaImage.getBoundingClientRect();

    switch (parentAlignment) {
      case 'center':
      case 'middle':
        marginLeft = `${mediaRect.width * (50 / percentageWidth - 0.5)}px`;
        break;
      case 'right':
        marginLeft = `${mediaRect.width * (100 / percentageWidth - 1)}px`;
        break;
      default:
        marginLeft = calculateElementOverlayLeftMargin(mediaImage);
        break;
    }

    marginLeft = `${parseInt(marginLeft) * (1 / window.PMW.emails.ZOOM_VALUE)}px`;
    return marginLeft;
  }

  function calculateComponentOverlayLeftMargin(componentElement: HTMLElement): string {
    return calculateMediaOverlayLeftMargin(componentElement);
  }

  function getElementResponsiveWidth(element: HTMLElement): string {
    const STYLE_SEPARATOR = ';';
    const STYLE_KEY_VALUE_SEPARATOR = ':';

    const deviceStylePrefix = {
      [window.PMW.emails.DEVICE_DESKTOP]: '',
      [window.PMW.emails.DEVICE_MOBILE]: window.PMW.emails.MOBILE_CSS_PROP_PREFIX,
      [window.PMW.emails.DEVICE_TABLET]: window.PMW.emails.TABLET_CSS_PROP_PREFIX,
    };

    const selectedDevice = editor.Devices.getSelected()?.id;

    if (!selectedDevice) {
      return element.style.width;
    }

    let width = null;

    element.style.cssText.split(STYLE_SEPARATOR).forEach((style) => {
      const parts = style.split(STYLE_KEY_VALUE_SEPARATOR);
      if (parts.length === 2 && parts[0].trim() === `${deviceStylePrefix[selectedDevice]}width`) {
        width = parts[1];
      }
    });

    if (!width) {
      if (element.classList.contains('pmw-media-type') && editor.Css.getRules('.pmw-media-type').length > 0) {
        width = editor.Css.getRules('.pmw-media-type')[0].getStyle().width as string;
      } else {
        width = element.style.width;
      }
    }

    width = width.replace(/!important/g, '').trim();

    return width;
  }

  function getMediaComponentOverlayContent(mediaImage: HTMLElement): HTMLElement {
    const ICON_DISPLAY_THRESHOLD = 125;
    const TEXT_DISPLAY_THRESHOLD = 75;

    const overlayDiv = document.createElement('div');

    Object.assign(overlayDiv.style, {
      position: 'absolute',
      left: `${mediaImage.offsetLeft}px`,
      top: '0',
      width: `${mediaImage.offsetWidth}px`,
      height: '100%',
      zIndex: 10,
      backgroundColor: 'rgba(20, 19, 37, 0.6)',
      pointerEvents: 'none',
      borderRadius: mediaImage.style.borderRadius,
      overflow: 'hidden',
    });

    const mediaRect = mediaImage.getBoundingClientRect();
    const showIcon = mediaRect.height > ICON_DISPLAY_THRESHOLD && mediaRect.width > ICON_DISPLAY_THRESHOLD;
    const showText = mediaRect.height > TEXT_DISPLAY_THRESHOLD && mediaRect.width > TEXT_DISPLAY_THRESHOLD;

    const contentContainer = document.createElement('div');
    const image = document.createElement('img');
    const text = document.createElement('p');

    image.src = getAssetUrl('email-maker-media-component-hover.png?t=1');

    const overlayTextKeyMap: Record<string, string> = {
      [window.PMW.emails.components.graphic.PMW_VIDEO]: 'pmwjs_email_video_overlay_text',
      [window.PMW.emails.components.graphic.PMW_STICKER]: 'pmwjs_email_sticker_overlay_text',
    };
    const pmwType = mediaImage.getAttribute('data-pmw-type') ?? '';
    text.innerText = window.i18next.t(overlayTextKeyMap[pmwType] ?? 'pmwjs_email_media_overlay_text');

    Object.assign(contentContainer.style, {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      padding: '8px',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      color: 'white',
      width: '100%',
    });

    Object.assign(image.style, {
      width: '100%',
      maxWidth: '40px',
      height: 'auto',
    });

    Object.assign(text.style, {
      padding: '0',
      margin: '0',
      color: '#FFF',
      'text-align': 'center',
      'font-family': '"Figtree", Helvetica, Arial, sans-serif',
      'font-size': showIcon ? '14px' : '12px',
      'font-style': 'normal',
      'font-weight': 400,
      'line-height': '150%',
    });

    if (showIcon) {
      contentContainer.appendChild(image);
    }
    if (showText) {
      contentContainer.appendChild(text);
    }

    overlayDiv.appendChild(contentContainer);

    return overlayDiv;
  }
};

export function getGettyMediaCount(grapesjsEditor: Editor, mediaType: string = window.PMW.emails.mediaType.IMAGE): number {
  let count = 0;
  const wrapper = grapesjsEditor.DomComponents.getWrapper();

  if (wrapper && grapesjsEditor.getComponents().models.length) {
    const mainContent = wrapper.find(`[data-gjs-type="${window.PMW.emails.components.container.PMW_CONTENT}"]`)[0];
    const property = getGettyCountPropForMediaType(mediaType);

    count = (mainContent?.get(property) as number) || 0;
  }

  return count;
}

export function getGettyCountPropForMediaType(mediaType: string): string {
  switch (mediaType) {
    case window.PMW.emails.mediaType.IMAGE:
      return window.PMW.emails.props.PREMIUM_IMAGE_COUNT;

    case window.PMW.emails.mediaType.VIDEO:
      return window.PMW.emails.props.PREMIUM_VIDEO_COUNT;

    default:
      return '';
  }
}

function enableComponentOutlines(grapesjsEditor: Editor): void {
  grapesjsEditor.runCommand('sw-visibility');
}

/**
 * runs command to set default device
 * @param grapesjsEditor
 */
function setDeviceOnLoad(grapesjsEditor: Editor): void {
  const currWidth = 0;
  let targetDevice = grapesjsEditor.Devices.getSelected();

  grapesjsEditor.Devices.getDevices().forEach((device) => {
    const isDefault = device.id === window.PMW.emails.DEVICE_DESKTOP;
    const widthMedia = (device.getWidthMedia() || document.body.getBoundingClientRect().width) as string;
    const widthMediaValue = parseInt(widthMedia, 10);
    const isApplicable = widthMediaValue >= document.body.getBoundingClientRect().width;
    const largerThanCurrent = widthMediaValue > currWidth;

    // skipping default as it will be selected if no other device is applying media queries currently
    if (!isDefault && isApplicable && largerThanCurrent) {
      targetDevice = device;
    }
  });

  if (!targetDevice) {
    return;
  }

  switch (targetDevice.id) {
    case window.PMW.emails.DEVICE_MOBILE:
      grapesjsEditor.runCommand(window.PMW.emails.commands.SET_DEVICE_MOBILE);
      break;
    case window.PMW.emails.DEVICE_TABLET:
      grapesjsEditor.runCommand(window.PMW.emails.commands.SET_DEVICE_TABLET);
      break;
    default:
      grapesjsEditor.runCommand(window.PMW.emails.commands.SET_DEVICE_DESKTOP);
  }
}

function updateShareBtnIcons(grapesjsEditor: Editor): void {
  const isSharedInTeam = $('#html-editor-container').attr('data-is-shared-in-team') === '1';
  grapesjsEditor.runCommand(window.PMW.emails.commands.UPDATE_SHARE_BUTTON_ICON, {isSharedInTeam: isSharedInTeam});
}

function syncTemplateName(grapesjsEditor: Editor): void {
  const loadedName = $('#html-editor-container').attr('data-template-name');
  const wrappers = grapesjsEditor.getWrapper()?.findType(window.PMW.emails.components.container.PMW_WRAPPER);
  if (!wrappers) {
    return;
  }

  if (wrappers.length === 0) {
    return;
  }

  const pmwWrapper = wrappers[0];
  const currentName = pmwWrapper?.get(window.PMW.emails.props.TEMPLATE_NAME) as string;

  if (loadedName !== currentName) {
    pmwWrapper?.set(window.PMW.emails.props.TEMPLATE_NAME, loadedName);
  }
}

/**
 * Handles the entire operation of removing background
 * @param {grapesjs.Editor} grapesjsEditor
 * @param {grapesjs.Component} component
 * @param {HTMLElement} el
 */
async function doRemoveBackground(grapesjsEditor: Editor, component: Component, el: HTMLElement): Promise<void> {
  const imageHashedFilename = component.get(window.PMW.emails.props.HASHED_FILENAME) as string;
  if (!imageHashedFilename) {
    openErrorModal({
      debugDetails: `No image hashedFilename for component: ${JSON.stringify(component)}`,
    });
  }

  el.classList.add('disabled');
  showLoading('removingBackground', {text: window.i18next.t('pmwjs_removing_background')});

  try {
    const response = await removeImageBackground(imageHashedFilename);
    const removedImageBackgroundScreenURL = getRemovedImageBackgroundScreenURLForEmailEditor(imageHashedFilename);

    setRemoveBackgroundImageToSrc(removedImageBackgroundScreenURL, el, grapesjsEditor, component);
    if (response) {
      postRemainingBackgroundCredits(response.remainingRemoveBackgroundCredits);
    }
  } catch (e: any) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (e.code === 'unprocessable_image') {
      openErrorModal({
        message: window.i18next.t('pmwjs_remove_bg_unprocessable_image'),
        log: false,
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    } else if (e.code === 'insufficient_credits') {
      openErrorModal({
        message: window.i18next.t('pmwjs_remove_bg_failed_insufficient_credits_desc'),
        log: false,
      });
    } else {
      openErrorModal({
        message: window.i18next.t('pmwjs_remove_bg_failed_desc'),
        log: false,
      });
    }
  } finally {
    hideLoading('removingBackground');
    el.classList.remove('disabled');
  }
}

/**
 * Need to use the png extension as webp throws errors when cropped
 * Need to trim the last ? as email editor backend code can't handle it. Example functions S3repositoryfacade/getCroppedEmailMediaURLs, doesUserEmailMediaExist
 * @param imageHashedFilename
 */
function getRemovedImageBackgroundScreenURLForEmailEditor(imageHashedFilename: string): string {
  let removedImageBackgroundScreenURL = getRemovedImageBackgroundScreenURL(imageHashedFilename);
  removedImageBackgroundScreenURL = `${removedImageBackgroundScreenURL.slice(0, removedImageBackgroundScreenURL.lastIndexOf('.'))}.png`;
  return removedImageBackgroundScreenURL;
}

function initPredefinedEditorCommands(opt: PrimaryConfigOptions): void {
  const {editor} = opt;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const codeViewer = editor && (editor.CodeManager.getViewer('CodeMirror').clone() as CustomCodeViewer);
  const container = document.createElement('div');
  const pfx = opt.pfx ?? '';
  const cmds = editor.Commands;

  codeViewer.set({
    codeName: 'htmlmixed',
    theme: opt.codeViewerTheme,
  });

  cmds.add(window.PMW.emails.commands.GET_INLINED_HTML, {
    run(grapesjsEditor, _sender, opts = {}) {
      let html = grapesjsEditor.getHtml();
      const wrapper = grapesjsEditor.getWrapper();

      if (wrapper) {
        const pmwWrapper = wrapper.findType(window.PMW.emails.components.container.PMW_WRAPPER);

        if (pmwWrapper.length > 0) {
          html = pmwWrapper[0].toHTML();
        }
      }

      const juicePreppedHtml = preProcessHtmlForJuice(html);
      const tmpl = `${juicePreppedHtml}<style>${editor.getCss()}</style>`;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const juiced = juice(tmpl, {...opts, xmlMode: false});
      return postProcessJuicedHtml(juiced);
    },
  });

  function preProcessHtmlForJuice(html: string): string {
    return he.decode(html);
  }

  function postProcessJuicedHtml(juiced: string): string {
    return juiced.replace(/<br[^>]*><\/br>/gi, '<br>');
  }

  cmds.add(window.PMW.emails.commands.EXPORT_TEMPLATE, {
    run(grapesjsEditor: Editor, sender: Component) {
      const md = grapesjsEditor.Modal;
      let viewer = codeViewer.editor;
      md.setTitle(opt.modalTitleExport!);
      // Init code viewer if not yet instantiated
      if (!viewer) {
        const txtarea = document.createElement('textarea');
        if (opt.modalLabelExport) {
          const labelEl = document.createElement('div');
          labelEl.className = `${pfx}export-label`;
          labelEl.innerHTML = opt.modalLabelExport;
          container.appendChild(labelEl);
        }
        container.appendChild(txtarea);
        codeViewer.init(txtarea);
        viewer = codeViewer.editor;

        viewer.setOption('lineWrapping', 1);
      }
      md.setContent(container);
      const tmpl = `${editor.getHtml()}<style>${editor.getCss()}</style>`;
      codeViewer.setContent(opt.inlineCss ? juice(tmpl, {}) : tmpl);
      md.open();
      viewer.refresh();
      if (sender && sender.set) {
        sender.set('active', 0);
      }
    },
  });
}

function initPredefinedStorageCommands(opt: PrimaryConfigOptions): void {
  const {editor} = opt;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const codeViewer = editor && (editor.CodeManager.getViewer('CodeMirror').clone() as CustomCodeViewer);
  const btnImp = document.createElement('button');
  const container = document.createElement('div');
  const pfx = opt.pfx || '';
  const cmds = editor.Commands;

  btnImp.innerHTML = 'Import';
  btnImp.className = `${pfx}btn-prim ${pfx}btn-import`;
  btnImp.onclick = (): void => {
    const code = codeViewer.editor.getValue();
    editor.DomComponents.getWrapper()?.set('content', '');
    editor.setComponents(code);
    editor.Modal.close();
  };
  // Init code viewer
  codeViewer.set({
    codeName: 'htmlmixed',
    theme: opt.codeViewerTheme,
    readOnly: 0,
  });

  cmds.add(window.PMW.emails.commands.OPEN_IMPORT_TEMPLATE, {
    run(_grapesjsEditor: Editor, sender: Component) {
      const md = editor.Modal;
      let viewer = codeViewer.editor;
      md.setTitle(opt.modalTitleImport!);
      // Init code viewer if not yet instantiated
      if (!viewer) {
        const txtarea = document.createElement('textarea');
        if (opt.modalLabelImport) {
          const labelEl = document.createElement('div');
          labelEl.className = `${pfx}import-label`;
          labelEl.innerHTML = opt.modalLabelImport;
          container.appendChild(labelEl);
        }
        container.appendChild(txtarea);
        container.appendChild(btnImp);
        codeViewer.init(txtarea);
        viewer = codeViewer.editor;
      }
      md.setContent('');
      md.setContent(container);
      codeViewer.setContent(opt.importPlaceholder || '');
      md.open();
      viewer.refresh();
      if (sender) {
        sender.set('active', 0);
      }
    },
  });
}

function isUserPremium(): boolean {
  return window.PMW.getUserPremiumLevel() > USER_PREMIUM_LEVELS.PAYG;
}

function setRemoveBackgroundImageToSrc(destURLScreen: string, _el: HTMLElement, editor: Editor, component: Component): void {
  component.set(window.PMW.emails.props.REMOVED_BACKGROUND_URL, destURLScreen);
  editor.runCommand(window.PMW.emails.commands.SET_IMAGE_SRC, {
    component,
    source: component.get(window.PMW.emails.props.CONTENT_SOURCE),
    hashedFilename: component.get(window.PMW.emails.props.HASHED_FILENAME),
    backgroundRemovedURL: destURLScreen,
  });
}

function getHashedFileNamesFromType(editor: Editor, type: string): string {
  const fileNames = new Set();
  editor.getWrapper()?.onAll((component) => {
    if (component.is(type)) {
      const fileName = component.get(window.PMW.emails.props.HASHED_FILENAME) as string;

      if (fileName) {
        fileNames.add(fileName);
      }
    }
  });

  return Array.from(fileNames).join(',') || '';
}

/**
 * Set credits to new decremented credit
 * @param {int|string} credits
 */
function postRemainingBackgroundCredits(credits: number): void {
  window.PMW.emails.NUM_REMOVE_BACKGROUND_CREDITS = credits;
  $('.js-bg-credits').text(window.PMW.emails.NUM_REMOVE_BACKGROUND_CREDITS);
}

function showHeaderForWrapperSidePanel(): void {
  const header = $('.email-editor-mobile-style-header');
  const headerContent = $('.email-editor-header-content');
  const mobileHandlebar = $('.mobile-handlebar');
  const styleManagerContainer = $('.html-editor-style-manager');

  headerContent.removeClass('_hidden');
  header.removeClass('height-auto spacing-p-0');
  mobileHandlebar.removeClass('spacing-m-2');
  styleManagerContainer.removeClass('-no-header');
}

function hideHeaderForWrapperOnSidePanelClose(): void {
  const header = $('.email-editor-mobile-style-header');
  const headerContent = $('.email-editor-header-content');
  const mobileHandlebar = $('.mobile-handlebar');
  const styleManagerContainer = $('.html-editor-style-manager');

  headerContent.addClass('_hidden');
  header.addClass('height-auto spacing-p-0 hidden-desktop');
  mobileHandlebar.addClass('spacing-m-2');
  styleManagerContainer.addClass('-no-header');
}

export function getMergeTagsFromOptionId(text: string, defaultValue: string | null = null): string {
  return `[[${capitalizeFirstLetter(text)}${defaultValue ? `::${defaultValue}` : ''}]]`;
}

export function hydrateMergeTagsReduxFromComponent(component: Component): void {
  if (!component.is(window.PMW.emails.components.text.PMW_TEXT)) {
    return;
  }

  const content = component.getEl()?.innerText;

  if (!content?.trim()) {
    window.PMW.redux.store.dispatch(removeAllTagsForComponent({ccid: component.ccid}));
    return;
  }

  const mergeTagPattern = getPMWMergeTagRegex();
  let match;
  const mergeTags = new Set<string>();
  let defaultValueHashMap = {};

  while ((match = mergeTagPattern.exec(content)) !== null) {
    if (match.length <= 1) {
      continue;
    }

    const mergeTagKey = lowerCaseFirstLetter(match[1]);

    if (match.length > 2) {
      const defaultValue = match[2];
      defaultValueHashMap = {
        ...defaultValueHashMap,
        ...(defaultValue && {[mergeTagKey]: defaultValue}),
      };
    }

    mergeTags.add(mergeTagKey);
  }

  if (mergeTags.size > 0) {
    window.PMW.redux.store.dispatch(
      populateTagsForComponent({
        ccid: component.ccid,
        mergeTagKeys: Array.from(mergeTags),
        defaultValueHashmap: defaultValueHashMap,
      })
    );
  }
}
